import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Map, { Source, Layer, Popup, Marker, ScaleControl, MapRef } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import pathfinderApi, { PathfinderDataPoint, PathfinderRoute, CoTravelerResult, CoTravelerEncounter } from '../../services/pathfinderApi';
import { Box, IconButton, Tooltip, Popover, Paper, Typography, Grid, Card, useTheme, Divider, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { 
  Map as MapLegendIcon,
  PictureInPicture as DwellInsetIcon,
  FormatListNumbered as RouteSequenceIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ClearAll as ClearAllIcon,
  Add as ZoomInIcon,
  Remove as ZoomOutIcon,
  Navigation as CompassIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  MyLocation as MyLocationIcon,
  Satellite as SatelliteIcon,
  Straighten as MeasureIcon,
  Delete as DeleteIcon,
  FilterAlt as FilterByMapIcon,
  Settings as MapSettingsIcon,
  CleaningServices as CleanupIcon,
  KeyboardArrowUp as CollapseToolbarIcon,
  KeyboardArrowDown as ExpandToolbarIcon,
  TableChart as AdtechTableIcon,
  Streetview as StreetViewIcon,
  Videocam as CameraToolIcon,
  ViewTimeline as TimeSliderIcon,
  Summarize as ProductExportIcon,
  AutoStories as NarrativeIcon,
  Search as SearchIcon,
  HelpOutline as ProductTourIcon,
} from '@mui/icons-material';
import { DrawCircle } from './AreaSearch';

const ROUTE_SEQUENCE_BADGE_LAYERS = ['route-sequence-badge-circles', 'route-sequence-badge-text'] as const;
import { useNarrativeStore, type StoryCameraContext } from '../../stores/narrativeStore';
import { MapStyleType } from '@signals/cop-ui/components/MapStyleControl';
import MapLegend, { type CameraCategoryLegendItem } from './MapLegend';
import ProductExportDialog from './ProductExportDialog';
import FullscreenProductTour from './FullscreenProductTour';
import AdtechDeviceActions from './AdtechDeviceActions';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { CAMERA_FACET_UNKNOWN, useCameraLocationsOverlay } from '@signals/app-shell/hooks/useCameraLocationsOverlay';
import {
  CAMERA_OVERLAY_LAYER_IDS,
  parseCameraFeatureProperties,
  type CameraLocationProperties,
} from '@signals/app-shell/services/cameraOverlayService';
import CameraLocationsMapLayers from '@signals/app-shell/components/CameraLocationsMapLayers';
import CameraLocationPopupContent from '@signals/app-shell/components/CameraLocationPopupContent';
import type { PathfinderCameraControls } from './MapLayerControlsDialog';
import { useReferenceLayers } from '@signals/app-shell/hooks/useReferenceLayers';
import ReferenceLayersMapLayers, {
  referenceLayerIdFromMapLayer,
  referenceLayerInteractiveIds,
} from '@signals/app-shell/components/map/ReferenceLayersMapLayers';
import ReferenceFeaturePopupContent from '@signals/app-shell/components/map/ReferenceFeaturePopupContent';
import {
  IntelPopupCard,
  INTEL_POPUP_CLASS,
  type IntelAction,
} from '@signals/app-shell/components/map/IntelPopup';
import CamerasInViewStrip, { type CameraInView } from '@signals/app-shell/components/map/CamerasInViewStrip';
import CameraCallouts, {
  cameraCalloutKey,
  defaultCalloutOffset,
  type CameraCallout,
} from '@signals/app-shell/components/map/CameraCallouts';
import DwellInsets, {
  defaultInsetOffset,
  dwellInsetKey,
  type DwellInset,
} from '@signals/app-shell/components/map/DwellInsets';
import MapMeasureLayers from '@signals/app-shell/components/map/MapMeasureLayers';
import MapMeasureReadout from '@signals/app-shell/components/map/MapMeasureReadout';
import { nextMeasurePoints, type MeasureMode } from '@signals/app-shell/services/mapMeasure';
import {
  buildRouteSequence,
  routeSequenceBadges,
  type RouteSequenceDwell,
  type RouteSequenceEntry,
  type RouteSequencePoint,
} from '@signals/app-shell/services/routeSequence';
import { useSettingsStore } from '@signals/app-shell/stores';
import { getPreferredTimeZone } from '@signals/app-shell/utils/time';
import RouteSequencePanel from './RouteSequencePanel';
import {
  STACKED_BADGE_CIRCLE_PAINT,
  STACKED_BADGE_TEXT_LAYOUT,
  stackBadges,
} from '@signals/app-shell/services/badgeStacking';
import { useKeepLayersOnTop } from '@signals/app-shell/components/map/useKeepLayersOnTop';
import StoryEventMapLabels, { useStoryBadgesShowing } from './StoryEventMapLabels';
import {
  filterCamerasNearPoints,
  loadCameraNearTracksPrefs,
  saveCameraNearTracksPrefs,
  type CameraNearTracksPrefs,
  type LonLat,
} from '@signals/app-shell/services/cameraProximity';
import {
  cameraHeading,
  streetViewDeepLink,
  streetViewPreviewsOn,
  useCameraStreetViewSettings,
} from '@signals/app-shell/services/cameraStreetViewService';
import { useAuthStore } from '@signals/app-shell/stores';
import { PathfinderCleanupDrawControl, PathfinderCleanupPanel } from './PathfinderCleanupTool';
import { colorForAdtechId } from '../../utils/pathfinderColors';

import {
  DARK_MAP_POPUP_CLASS,
  PfPopupShell,
  PfPopupRow,
  PfPopupCoords,
  pfActionBtnStyle,
  useDarkMapPopupChrome,
} from '@signals/app-shell/components/map/DarkMapPopup';

/** Trailing digits of an entity id ("Student-32", "Student-07 - Phone") → "32" / "07". */
function entityNumberDesignator(rawId?: string | null): string {
  if (!rawId) return '';
  const entity = String(rawId).split(' - ')[0].trim();
  const match = entity.match(/(\d+)\s*$/);
  if (!match) return '';
  return match[1].padStart(2, '0');
}

/** Dwell row shape for map circles (matches parent / API payloads). */
export interface PathfinderMapDwellLocation {
  id: number;
  entity_id: string;
  device_type?: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
  longitude: number;
  latitude: number;
  place_name?: string;
  place_address?: string;
}

/** Invisible layer that reference layers insert beneath (keeps them under app layers). */
const REF_LAYERS_ANCHOR_ID = 'ref-layers-anchor';

/**
 * Convert a hard alpha mask into a signed-distance-field image using the same
 * convention Mapbox uses for glyphs (radius 8px, cutoff 0.25 → edge at alpha
 * 191). A plain mask registered with `sdf: true` renders aliased and the halo
 * has nothing to grow into; a proper field gives crisp tinted icons + outline.
 */
const maskToSdf = (img: ImageData, radius = 8, cutoff = 0.25): ImageData => {
  const { width: w, height: h, data } = img;
  const inside = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) inside[i] = data[i * 4 + 3] > 127 ? 1 : 0;
  const out = new ImageData(w, h);
  const r = Math.ceil(radius);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      const self = inside[idx];
      let best = radius * radius;
      for (let dy = -r; dy <= r; dy++) {
        const yy = y + dy;
        for (let dx = -r; dx <= r; dx++) {
          const xx = x + dx;
          const other = xx < 0 || yy < 0 || xx >= w || yy >= h ? 0 : inside[yy * w + xx];
          if (other !== self) {
            const d2 = dx * dx + dy * dy;
            if (d2 < best) best = d2;
          }
        }
      }
      // Positive outside, negative inside (TinySDF convention).
      const d = (self ? -1 : 1) * Math.sqrt(best);
      const a = Math.max(0, Math.min(255, Math.round(255 - 255 * (d / radius + cutoff))));
      out.data[idx * 4] = 255;
      out.data[idx * 4 + 1] = 255;
      out.data[idx * 4 + 2] = 255;
      out.data[idx * 4 + 3] = a;
    }
  }
  return out;
};

interface PathfinderMapGLProps {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  studentColors: Record<string, string>;
  mapSettings: {
    center: [number, number];
    zoom: number;
    style: string;
  };
  showDataPoints: boolean;
  showRouteLines: boolean;
  showDirectionArrows: boolean;
  showHeatmap: boolean;
  enableClustering: boolean;
  coTravelerResults?: CoTravelerResult[];
  showCoTravelerConnections?: boolean;
  showCoTravelerPoints?: boolean;
  showCoTravelerLines?: boolean;
  showCoTravelerHeatmap?: boolean;
  timeRange?: { start: Date; end: Date } | null;
  currentPlaybackTime?: Date | null; // Current time during playback for leading edge arrow
  // Slider window currently drawn on the map. Null while the full range is shown.
  playbackWindow?: { start: Date; end: Date } | null;
  onMapChange?: (center: [number, number], zoom: number) => void;
  followMode?: boolean; // If true, allow automatic camera updates
  // Dwell locations
  dwellLocations?: PathfinderMapDwellLocation[];
  showDwells?: boolean;
  // Ring the SRP nearest the selected student's current position.
  highlightClosestSrp?: boolean;
  selectedStudentId?: string;
  // Route gap / implausible-hop handling (org defaults + session overrides)
  hideGaps?: boolean;
  hideImplausibleMovement?: boolean;
  implausibleMaxSpeedKmh?: number;
  gapMaxTimeMinutes?: number;
  gapMaxDistanceM?: number;
  // Digital PoL
  digitalPoLData?: any[];
  showDigitalPoL?: boolean;
  // Area search / circle drawing props
  isDrawingMode?: boolean;
  drawCircles?: DrawCircle[];
  defaultRadius?: number;
  onCircleDrawn?: (center: [number, number], radius: number) => void;
  // Annotation drawing props
  isAnnotationMode?: boolean;
  onAnnotationDrawn?: (geometry: any) => void;
  onAnnotationModeCancel?: () => void;
  // Geofence overlay (Pathfinder Geofence tab). Drawing reuses annotation mode.
  geofences?: Array<{
    id: number;
    name: string;
    geometry: { type: 'Polygon'; coordinates: number[][][] };
  }>;
  selectedGeofenceId?: number | null;
  // Overwatch (GDELT media) article markers — geolocated news articles. Each
  // article may reference multiple places; every location is plotted.
  overwatchArticles?: Array<{
    id: string;
    title: string;
    latitude?: number;
    longitude?: number;
    is_translated?: boolean;
    primary_category?: string;
    locations?: Array<{ latitude: number; longitude: number; name?: string; country?: string }>;
  }>;
  // AdTech (commercial mobile-location) signals plotted from an Area Search.
  // Each is one raw observation; many can share a registration_id.
  adtechSignals?: Array<{
    registration_id: string;
    latitude: number;
    longitude: number;
    timestamp?: string | null;
    carrier?: string | null;
    country?: string | null;
    accuracy?: number | null;
    ip_address?: string | null;
    timezone?: string | null;
    forensic_flag?: string | null;
  }>;
  // Registration id the user has focused (from the bottom results table). Its
  // points render larger/brighter so the operator can pick a device out.
  adtechFocusId?: string | null;
  // AdTech results-table visibility control (toolbar button fully hides/shows
  // the floating table without discarding the loaded signals).
  hasAdtechResults?: boolean;
  adtechTableVisible?: boolean;
  onToggleAdtechTable?: () => void;
  // Itinerary submissions
  itinerarySubmissions?: Array<{
    id?: number;
    student_number: string;
    route_run_datetime: string;
    route_entries: Array<{
      id?: number;
      latitude: number;
      longitude: number;
      location_name_address: string;
      arrive_datetime: string;
      depart_datetime: string;
      route_type: string;
      route_component: string;
    }>;
  }>;
  showItinerary?: boolean;
  // Selected trainee name for legend
  selectedTraineeName?: string;
  selectedTraineeDevices?: Array<{ deviceType: string; color: string }>;
  // Per-cotraveler legend swatches (name + resolved color). Filtered to
  // currently-visible cotravelers so the legend mirrors what's on screen.
  coTravelerLegendItems?: Array<{ name: string; color: string; entityType?: string }>;
  // Uploaded file info for legend
  uploadedFiles?: Array<{ name: string; visible: boolean; pointCount: number }>;
  onToggleUploadedFile?: (name: string, visible: boolean) => void;
  // RF Signal markers (Pathfinder RF module)
  signalMarkers?: Array<{ latitude: number; longitude: number; type: 'wifi' | 'bluetooth' | 'cellular'; label: string; count: number; device_type?: string; manufacturer?: string }>;
  showSignalMarkers?: boolean;
  // Biometric overlay (Pathfinder Biometrics tab) - highlight where the user was in a given HR zone
  biometricHighlights?: Array<{
    latitude: number;
    longitude: number;
    value: number;        // sample value (e.g. bpm)
    zone?: string;        // 'rest' | 'light' | 'moderate' | 'hard' | 'max'
    color: string;        // hex color matching the panel chip
    timestamp?: string;   // ISO sample time
    label?: string;       // tooltip-friendly description (e.g. "172 bpm — Hard")
  }>;
  // Emits the current map viewport bounds whenever the camera settles. Used by
  // the parent's "Crop tables to map view" toggle to filter right-panel tabs
  // (Charts, Co-Travelers, Dwell, Digital PoL, Annotations) to what's actually
  // on screen. Camera control is intentionally NOT touched — this is purely an
  // outgoing notification.
  onMapBoundsChange?: (bounds: { west: number; south: number; east: number; north: number } | null) => void;
  // "Crop tables to map view" — parent-owned toggle state surfaced on the
  // map toolbar so analysts can flip it without scrolling back to the page
  // header. The map itself never changes camera in response; only the
  // right-side tabs (Charts, Co-Travelers, Dwell, Digital PoL,
  // Annotations) crop to the current viewport.
  filterByMapExtent?: boolean;
  onToggleFilterByMapExtent?: () => void;
  pttUtterances?: Array<{
    id: number;
    latitude?: number | null;
    longitude?: number | null;
    transcript?: string | null;
    entity_id?: string | null;
    device_id: string;
    sender?: string | null;
    team?: string | null;
    started_at?: string;
    ended_at?: string;
  }>;
  showPttMessages?: boolean;
  onShowPttMessagesChange?: (show: boolean) => void;
  /** PTT icons cluster on their own switch, independent of track points. */
  clusterPttMessages?: boolean;
  /** Playback window; icons outside it fade instead of disappearing. */
  pttPlaybackWindow?: { start: Date; end: Date } | null;
  /** Sender → colour map shared with the PTT panel so bubbles and avatars match. */
  pttSenderColors?: Record<string, string>;
  /** Popup "Open in panel" — switches the right rail to the PTT tool and scrubs to the line. */
  onPttOpenInPanel?: (id: number) => void;

  // Optional change handlers wired to the new "Map Layers" dialog
  // (`MapLayerControlsDialog`). When provided, the map renders a Layers
  // button on its right-side toolbar that opens a centered popup with
  // these toggles. If a handler is omitted the corresponding switch is
  // still shown but treated as read-only - the legacy slider-based
  // controls remain the source of truth in that case so this is safe to
  // adopt incrementally.
  // showItinerary / showCameras / showDwellLabels are toggled from the Map
  // Settings dialog (mounted by CompactTimeSlider) and passed in read-only.
  onShowDataPointsChange?: (show: boolean) => void;
  onShowRouteLinesChange?: (show: boolean) => void;
  onShowDirectionArrowsChange?: (show: boolean) => void;
  onShowHeatmapChange?: (show: boolean) => void;
  onEnableClusteringChange?: (enable: boolean) => void;
  onShowDwellsChange?: (show: boolean) => void;
  onShowCoTravelerPointsChange?: (show: boolean) => void;
  onShowCoTravelerLinesChange?: (show: boolean) => void;
  onShowCoTravelerHeatmapChange?: (show: boolean) => void;
  whiteCellInterval?: number | null;
  onWhiteCellIntervalChange?: (interval: number | null) => void;
  // Opens the centered Map Settings dialog (mounted by CompactTimeSlider so
  // it has access to playback state). Provided by PathfinderPage so the
  // map's toolbar button and the slider's gear button can both flip the
  // same boolean.
  onOpenMapSettings?: () => void;
  /** The page's time slider is minimized to its chip; the toolbar offers a restore button. */
  timeSliderMinimized?: boolean;
  onRestoreTimeSlider?: () => void;
  /** Camera-overlay visibility (Map Settings "Cameras" switch). */
  showCameras?: boolean;
  /** Reports the loaded camera count (0 when hidden) for the Map Settings label. */
  onCameraCountChange?: (count: number) => void;
  /** Publishes the camera filter controls (facets, near-track prefs, display) so the
   *  Map Settings dialog — mounted elsewhere — can render them. */
  onCameraControlsChange?: (controls: PathfinderCameraControls | null) => void;
  /** Dwell-label visibility (Map Settings "Dwell labels" switch; page-owned). */
  showDwellLabels?: boolean;
  /** Lets the page fit the map to a reference layer's bbox (Map Settings zoom-to). */
  onRegisterFitBounds?: (fn: ((bbox: [number, number, number, number]) => void) | null) => void;
  /** When set, the "cameras in view" strip is portalled into this element
   *  (PathfinderPage docks it onto the floating timeline card) instead of
   *  floating over the map. */
  camerasInViewDockEl?: HTMLElement | null;
  // Fired by the admin GPS-cleanup tool after a successful quarantine,
  // delete, or restore. PathfinderPage uses this to re-fetch tracks and
  // dwells so the map reflects the cleanup immediately - without it the
  // user has to manually nudge the time slider or reload.
  onRefreshDataRequested?: () => void;
  /** Optional course scope for popup FLAG → Saved Devices watchlist. */
  courseId?: string;
  /** Load AdTech PoL from an AdTech map popup action. */
  onLoadAdtechPol?: (registrationIds: string[], label?: string) => void;
  adtechPolLoading?: boolean;
}

const PathfinderMapGL: React.FC<PathfinderMapGLProps> = ({
  dataPoints,
  routes,
  studentColors,
  mapSettings,
  showDataPoints,
  showRouteLines,
  showDirectionArrows,
  showHeatmap,
  enableClustering,
  coTravelerResults = [] as CoTravelerResult[],
  showCoTravelerConnections: _showCoTravelerConnections = false,
  showCoTravelerPoints: _showCoTravelerPoints = false,
  showCoTravelerLines: _showCoTravelerLines = false,
  showCoTravelerHeatmap: _showCoTravelerHeatmap = false,
  timeRange,
  currentPlaybackTime,
  playbackWindow = null as { start: Date; end: Date } | null,
  onMapChange,
  followMode = false,
  // Dwell locations
  dwellLocations = [] as PathfinderMapDwellLocation[],
  showDwells = true,
  highlightClosestSrp = false,
  selectedStudentId,
  // Route gap / implausible-hop handling
  hideGaps = false,
  hideImplausibleMovement = true,
  implausibleMaxSpeedKmh = 160,
  gapMaxTimeMinutes = 30,
  gapMaxDistanceM = 500,
  // Digital PoL
  digitalPoLData = [] as any[],
  showDigitalPoL = false,
  pttUtterances = [] as NonNullable<PathfinderMapGLProps['pttUtterances']>,
  showPttMessages = true,
  clusterPttMessages = true,
  pttPlaybackWindow = null,
  pttSenderColors,
  onPttOpenInPanel,
  // Area search / circle drawing props
  isDrawingMode = false,
  drawCircles = [] as DrawCircle[],
  defaultRadius = 100,
  onCircleDrawn,
  // Annotation drawing props
  isAnnotationMode = false,
  onAnnotationDrawn,
  onAnnotationModeCancel: _onAnnotationModeCancel,
  // Geofence overlay
  geofences = [] as NonNullable<PathfinderMapGLProps['geofences']>,
  selectedGeofenceId = null,
  // Overwatch article markers
  overwatchArticles = [] as NonNullable<PathfinderMapGLProps['overwatchArticles']>,
  // AdTech area-search signals
  adtechSignals = [] as NonNullable<PathfinderMapGLProps['adtechSignals']>,
  adtechFocusId = null,
  hasAdtechResults = false,
  adtechTableVisible = true,
  onToggleAdtechTable,
  // Itinerary submissions
  itinerarySubmissions = [] as NonNullable<PathfinderMapGLProps['itinerarySubmissions']>,
  showItinerary = false,
  // Map Layers dialog wiring (optional). Falsy handlers turn the switches
  // into read-only indicators - safe default while callers migrate.
  onShowDataPointsChange,
  onShowRouteLinesChange,
  onShowDirectionArrowsChange,
  onShowHeatmapChange,
  onEnableClusteringChange,
  onShowDwellsChange,
  onShowCoTravelerPointsChange,
  onShowCoTravelerLinesChange,
  onShowCoTravelerHeatmapChange,
  whiteCellInterval = null,
  onWhiteCellIntervalChange,
  // Selected trainee name for legend
  selectedTraineeName,
  selectedTraineeDevices = [] as Array<{ deviceType: string; color: string }>,
  coTravelerLegendItems = [] as Array<{ name: string; color: string; entityType?: string }>,
  uploadedFiles = [] as Array<{ name: string; visible: boolean; pointCount: number }>,
  onToggleUploadedFile,
  signalMarkers = [] as NonNullable<PathfinderMapGLProps['signalMarkers']>,
  showSignalMarkers = false,
  biometricHighlights = [] as NonNullable<PathfinderMapGLProps['biometricHighlights']>,
  onMapBoundsChange,
  filterByMapExtent = false,
  onToggleFilterByMapExtent,
  onOpenMapSettings,
  timeSliderMinimized = false,
  onRestoreTimeSlider,
  showCameras: showCamerasProp,
  onCameraCountChange,
  onCameraControlsChange,
  showDwellLabels: showDwellLabelsProp = false,
  onRegisterFitBounds,
  camerasInViewDockEl = null,
  onRefreshDataRequested,
  courseId,
  onLoadAdtechPol,
  adtechPolLoading = false,
}: PathfinderMapGLProps) => {
  const { singular } = useParticipantLabels();
  // Polygon drawing state for annotations
  const [annotationVertices, setAnnotationVertices] = useState<[number, number][]>([]);
  
  // Legend visibility state
  const [showLegend, setShowLegend] = useState(false);
  // Current viewport extent (set on move end); scopes the legend to what is visible.
  const [viewBounds, setViewBounds] = useState<{ west: number; south: number; east: number; north: number } | null>(null);
  const [popupActionMsg, setPopupActionMsg] = useState<string | null>(null);
  const [popupActionBusy, setPopupActionBusy] = useState(false);

  const flagEntityToWatchlist = useCallback(async (entityId: string, label?: string) => {
    if (!entityId || popupActionBusy) return;
    setPopupActionBusy(true);
    setPopupActionMsg(null);
    try {
      await pathfinderApi.saveDevice({
        registrationId: entityId,
        name: label || entityId,
        deviceKind: 'student',
        courseId,
      });
      window.dispatchEvent(new Event('refresh-saved-devices'));
      setPopupActionMsg('Added to watchlist');
    } catch {
      setPopupActionMsg('Watchlist save failed');
    } finally {
      setPopupActionBusy(false);
    }
  }, [courseId, popupActionBusy]);
  // Camera visibility is owned by the page (Map Settings dialog "Cameras"
  // switch); the toolbar no longer has its own button.
  const showCameraLocations = showCamerasProp ?? false;
  // Story AI needs camera pass data even when the camera layer is hidden.
  const storyHudOpen = useNarrativeStore((s) => s.panelsOpen.story || s.panelsOpen.ai);
  const setStoryCameras = useNarrativeStore((s) => s.setStoryCameras);
  const cameraOverlay = useCameraLocationsOverlay(showCameraLocations || storyHudOpen);
  // Camera count for the Map Settings dialog (page re-renders the dialog with it).
  useEffect(() => {
    onCameraCountChange?.(showCameraLocations ? cameraOverlay.totalCount : 0);
  }, [onCameraCountChange, showCameraLocations, cameraOverlay.totalCount]);
  // Admin reference layers (boundaries / districts / POIs…). Shared store, so
  // the Map Settings dialog toggles are reflected here immediately.
  const referenceLayers = useReferenceLayers(true);
  const referenceInteractiveIds = useMemo(
    () => referenceLayerInteractiveIds(referenceLayers.layers),
    [referenceLayers.layers]
  );
  const {
    data: cameraLocationsData,
    featureCount: cameraFeatureCount,
    totalCount: cameraTotalCount,
    hasActiveFilters: cameraFiltersActive,
    facets: cameraFacets,
  } = cameraOverlay;
  // Pathfinder shows historic tracks, so by default only cameras near the loaded
  // track points are drawn; everything else stays off until the user widens it.
  const [cameraNearTracks, setCameraNearTracks] = useState<CameraNearTracksPrefs>(loadCameraNearTracksPrefs);
  const updateCameraNearTracks = useCallback((patch: Partial<CameraNearTracksPrefs>) => {
    setCameraNearTracks((prev) => {
      const next = { ...prev, ...patch };
      saveCameraNearTracksPrefs(next);
      return next;
    });
  }, []);
  const trackLonLats = useMemo<LonLat[]>(() => {
    if (!(showCameraLocations || storyHudOpen) || !cameraNearTracks.enabled) return [];
    const out: LonLat[] = [];
    for (const p of dataPoints) {
      const c = p.coordinates;
      if (!Array.isArray(c) || c.length < 2) continue;
      const lat = Number(c[0]);
      const lon = Number(c[1]);
      if (Number.isFinite(lat) && Number.isFinite(lon)) out.push([lon, lat]);
    }
    return out;
  }, [dataPoints, showCameraLocations, storyHudOpen, cameraNearTracks.enabled]);
  const cameraLayerData = useMemo(
    () =>
      cameraNearTracks.enabled && trackLonLats.length
        ? filterCamerasNearPoints(cameraLocationsData, trackLonLats, cameraNearTracks.radiusM)
        : cameraLocationsData,
    [cameraLocationsData, trackLonLats, cameraNearTracks.enabled, cameraNearTracks.radiusM],
  );
  const cameraShownCount = cameraLayerData.features.length;
  // Publish the filter controls for the Map Settings dialog. Every piece below is
  // either memoised in the hook or a primitive, so this only fires on real changes
  // (no render loop through the page's setState).
  const cameraControls = useMemo<PathfinderCameraControls>(
    () => ({
      overlay: {
        facets: cameraFacets,
        featureCount: cameraFeatureCount,
        totalCount: cameraTotalCount,
        hasActiveFilters: cameraFiltersActive,
        setCategoryVisible: cameraOverlay.setCategoryVisible,
        setSourceVisible: cameraOverlay.setSourceVisible,
        soloCategory: cameraOverlay.soloCategory,
        soloSource: cameraOverlay.soloSource,
        setAllCategoriesVisible: cameraOverlay.setAllCategoriesVisible,
        setAllSourcesVisible: cameraOverlay.setAllSourcesVisible,
        clearFilters: cameraOverlay.clearFilters,
        display: cameraOverlay.display,
        setDisplay: cameraOverlay.setDisplay,
      },
      proximity: {
        prefs: cameraNearTracks,
        onChange: updateCameraNearTracks,
        trackPointCount: trackLonLats.length,
        matchedCount: cameraShownCount,
      },
      shownCount: cameraShownCount,
    }),
    [
      cameraFacets,
      cameraFeatureCount,
      cameraTotalCount,
      cameraFiltersActive,
      cameraOverlay.setCategoryVisible,
      cameraOverlay.setSourceVisible,
      cameraOverlay.soloCategory,
      cameraOverlay.soloSource,
      cameraOverlay.setAllCategoriesVisible,
      cameraOverlay.setAllSourcesVisible,
      cameraOverlay.clearFilters,
      cameraOverlay.display,
      cameraOverlay.setDisplay,
      cameraNearTracks,
      updateCameraNearTracks,
      trackLonLats.length,
      cameraShownCount,
    ]
  );
  useEffect(() => {
    onCameraControlsChange?.(cameraControls);
  }, [onCameraControlsChange, cameraControls]);
  useEffect(() => () => onCameraControlsChange?.(null), [onCameraControlsChange]);
  // Street View (admin-gated): links on popups; thumbnails + "cameras in view" strip when a key is configured.
  const { settings: streetViewSettings } = useCameraStreetViewSettings();
  const streetViewEnabled = Boolean(streetViewSettings?.enabled);
  const streetViewPreviews = streetViewPreviewsOn(streetViewSettings);
  const [camerasInViewOpen, setCamerasInViewOpen] = useState(false);
  const [cameraToolMenuAnchor, setCameraToolMenuAnchor] = useState<HTMLElement | null>(null);
  // "Export product" dialog: one capture of the whole view plus every widget's data.
  const [productExportOpen, setProductExportOpen] = useState(false);
  const [tourReplay, setTourReplay] = useState(0);
  // Cameras pinned from the strip as floating Street View callouts (briefing
  // screenshots). Survives closing the strip so the capture can be clean.
  const [cameraCallouts, setCameraCallouts] = useState<CameraCallout[]>([]);
  const [cameraCalloutsVisible, setCameraCalloutsVisible] = useState(true);
  const cameraCalloutKeys = useMemo(() => new Set(cameraCallouts.map((c) => c.key)), [cameraCallouts]);
  const toggleCameraCallout = useCallback((item: CameraInView) => {
    const key = cameraCalloutKey(item);
    setCameraCallouts((prev) => {
      if (prev.some((c) => c.key === key)) return prev.filter((c) => c.key !== key);
      return [...prev, { key, item, ...defaultCalloutOffset(prev.length) }];
    });
    setCameraCalloutsVisible(true);
  }, []);
  const moveCameraCallout = useCallback((key: string, dx: number, dy: number) => {
    setCameraCallouts((prev) => prev.map((c) => (c.key === key ? { ...c, dx, dy } : c)));
  }, []);
  const resizeCameraCallout = useCallback((key: string, w: number) => {
    setCameraCallouts((prev) => prev.map((c) => (c.key === key ? { ...c, w } : c)));
  }, []);
  const removeCameraCallout = useCallback((key: string) => {
    setCameraCallouts((prev) => prev.filter((c) => c.key !== key));
  }, []);
  const [mapToolbarCollapsed, setMapToolbarCollapsed] = useState(true);

  // GPS cleanup tool state (gis-admin-gated, see PathfinderCleanupTool.tsx).
  // The polygon is held here so the draw control (inside <Map>) and the
  // panel (outside <Map>) can share it. Admin check is read from the
  // shared auth store so the toolbar button is hidden for everyone else.
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [cleanupPolygon, setCleanupPolygon] = useState<any | null>(null);
  // Bumped after a successful delete to force the draw control to wipe
  // the just-used polygon and re-enter draw_polygon mode. Lets the
  // analyst keep working in another spot without closing the panel.
  const [cleanupResetSignal, setCleanupResetSignal] = useState(0);
  // Mirrors the PIN-verified state from the panel. The draw control
  // stays inactive until this is true so a user can't draw a polygon
  // before they've unlocked the tool (the API already rejects without
  // the PIN, but it's confusing to be able to draw at all).
  const [cleanupPinVerified, setCleanupPinVerified] = useState(false);
  const currentUser = useAuthStore((s) => s.user);
  // GPS Cleanup is restricted to the gov Azure AD gis-admin group
  // (object id 77924e41-ac40-4aa4-87a5-7b4cc5c18f2a), not general admins.
  const GIS_ADMIN_GROUP_ID = '77924e41-ac40-4aa4-87a5-7b4cc5c18f2a';
  const canUseGpsCleanup = !!(
    currentUser?.roles?.includes('gis_admin') ||
    currentUser?.groups?.some((g) => {
      const gl = (g || '').trim().toLowerCase();
      return (
        gl === GIS_ADMIN_GROUP_ID ||
        gl === 'gis-admin' ||
        gl === 'gis-admins' ||
        gl === 'gis_admin' ||
        gl === 'gis administrators' ||
        gl === 'gis-administrators'
      );
    })
  );

  
  const [viewport, setViewport] = useState(() => {
    return {
      longitude: mapSettings.center[1] || -110.9265, // Default to Tucson if not provided
      latitude: mapSettings.center[0] || 32.2217,
      zoom: mapSettings.zoom || 11
    };
  });

  const [popupInfo, setPopupInfo] = useState<any>(null);
  useEffect(() => {
    setPopupActionMsg(null);
  }, [popupInfo?.longitude, popupInfo?.latitude, popupInfo?.layerId, popupInfo?.type]);
  useDarkMapPopupChrome(!!popupInfo);
  // Camera / reference-layer popups render on the shared intel card (Street View
  // thumbnails, layer styling) rather than the PfPopupShell command card.
  const intelPopupOpen =
    popupInfo?.type === 'camera' || popupInfo?.type === 'reference' || popupInfo?.properties?.marker_type === 'camera';
  // Dwell labels are toggled from Map Settings (page-owned state).
  const showDwellLabels = showDwellLabelsProp;
  const mapRef = React.useRef<MapRef | null>(null);
  const geocoderRef = React.useRef<MapboxGeocoder | null>(null);
  const [searchAnchor, setSearchAnchor] = useState<HTMLElement | null>(null);
  const [geocoderBox, setGeocoderBox] = useState<HTMLDivElement | null>(null);
  // Hand the page a stable "fit to bbox" so Map Settings can zoom to a reference layer.
  useEffect(() => {
    if (!onRegisterFitBounds) return;
    onRegisterFitBounds((bbox) => {
      mapRef.current?.fitBounds(
        [
          [bbox[0], bbox[1]],
          [bbox[2], bbox[3]],
        ],
        { padding: 60, duration: 800, maxZoom: 15 }
      );
    });
    return () => onRegisterFitBounds(null);
  }, [onRegisterFitBounds]);
  // 3D mode state
  const [is3DMode, setIs3DMode] = useState(false);
  
  // Fullscreen and measurement state
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Story narrative HUD over the map (rendered by PathfinderPage).
  const toggleHudPanel = useNarrativeStore((s) => s.togglePanel);
  const setHudPanelOpen = useNarrativeStore((s) => s.setPanelOpen);
  const setHudPanelMinimized = useNarrativeStore((s) => s.setPanelMinimized);
  const storyPanelOpen = useNarrativeStore((s) => s.panelsOpen.story);
  const storyPanelMinimized = useNarrativeStore((s) => s.panelMinimized.story);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState<[number, number][]>([]);
  const [measureMode, setMeasureMode] = useState<MeasureMode>('distance');
  const [ringSpacingM, setRingSpacingM] = useState(100);
  const [ringCount, setRingCount] = useState(1);
  const [ringOpacity, setRingOpacity] = useState(0.85);
  const [totalDistance, setTotalDistance] = useState<number>(0);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Theme for map style default
  const theme = useTheme();
  
  // Map style state (converted from old string to MapStyleType)
  // Default based on theme: light mode = streets, dark mode = dark
  const [mapStyle, setMapStyle] = useState<MapStyleType>(() => {
    const style = mapSettings.style.toLowerCase();
    if (style.includes('satellite-streets')) return 'satellite-streets';
    if (style.includes('satellite')) return 'satellite';
    if (style.includes('dark')) return 'dark';
    if (style.includes('light')) return 'light';
    if (style.includes('outdoors')) return 'outdoors';
    // Use theme-based default
    return theme.palette.mode === 'light' ? 'streets' : 'dark';
  });
  
  // Map style popover state
  const [mapStyleAnchor, setMapStyleAnchor] = useState<HTMLButtonElement | null>(null);

  // Reset annotation vertices when mode changes
  useEffect(() => {
    if (!isAnnotationMode) {
      setAnnotationVertices([]);
    }
  }, [isAnnotationMode]);

  // Generate GeoJSON for annotation polygon being drawn
  const annotationPolygonGeoJSON = useMemo((): GeoJSON.Feature | null => {
    if (annotationVertices.length < 2) return null;
    
    // Create a line for 2 points, polygon for 3+
    if (annotationVertices.length === 2) {
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: annotationVertices
        },
        properties: {}
      };
    }
    
    // Close the polygon for display
    const closedCoords = [...annotationVertices, annotationVertices[0]];
    return {
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: [closedCoords]
      },
      properties: {}
    };
  }, [annotationVertices]);

  // Saved geofence polygons (Pathfinder Geofence tab). Rendered as fill +
  // outline + label; the selected fence is highlighted.
  const geofencesGeoJSON = useMemo(() => {
    const features = (geofences || [])
      .filter((g) => g?.geometry?.coordinates?.length)
      .map((g) => ({
        type: 'Feature' as const,
        geometry: g.geometry,
        properties: {
          id: g.id,
          name: g.name,
          selected: g.id === selectedGeofenceId,
        },
      }));
    return { type: 'FeatureCollection' as const, features };
  }, [geofences, selectedGeofenceId]);

  // Overwatch media article markers (geolocated GDELT news). Each article is
  // expanded into one point per referenced location; falls back to the primary
  // lat/lng when a per-location list isn't present.
  const overwatchGeoJSON = useMemo(() => {
    const features: Array<{
      type: 'Feature';
      geometry: { type: 'Point'; coordinates: [number, number] };
      properties: { id: string; title: string; foreign: boolean; category: string; place: string };
    }> = [];
    for (const a of overwatchArticles || []) {
      const pts = (a.locations && a.locations.length > 0)
        ? a.locations
        : (a.latitude != null && a.longitude != null
            ? [{ latitude: a.latitude, longitude: a.longitude, name: undefined }]
            : []);
      for (const p of pts) {
        if (p.latitude == null || p.longitude == null) continue;
        features.push({
          type: 'Feature' as const,
          geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
          properties: {
            id: a.id,
            title: a.title,
            foreign: !!a.is_translated,
            category: a.primary_category || '',
            place: (p as any).name || '',
          },
        });
      }
    }
    return { type: 'FeatureCollection' as const, features };
  }, [overwatchArticles]);

  const adtechGeoJSON = useMemo(() => {
    const features: Array<{
      type: 'Feature';
      geometry: { type: 'Point'; coordinates: [number, number] };
      properties: {
        registration_id: string;
        timestamp: string;
        carrier: string;
        country: string;
        ip_address: string;
        forensic_flag: string;
        focused: boolean;
        color: string;
      };
    }> = [];
    for (const s of adtechSignals || []) {
      const lat = Number(s.latitude);
      const lon = Number(s.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
      const rid = s.registration_id || 'unknown';
      features.push({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [lon, lat] },
        properties: {
          registration_id: rid,
          timestamp: s.timestamp || '',
          carrier: s.carrier || '',
          country: s.country || '',
          ip_address: s.ip_address || '',
          forensic_flag: s.forensic_flag || '',
          focused: !!adtechFocusId && s.registration_id === adtechFocusId,
          color: colorForAdtechId(rid),
        },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [adtechSignals, adtechFocusId]);

  // Generate GeoJSON for drawn circles
  const circlesGeoJSON = useMemo(() => {
    if (drawCircles.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = drawCircles
      .filter(circle => circle.visible)
      .map(circle => {
        // Freeform polygon areas render their actual drawn ring; circular areas
        // fall back to a 64-point circle approximation of center + radius.
        if (Array.isArray(circle.polygon) && circle.polygon.length >= 3) {
          const ring = circle.polygon.map(([lon, lat]) => [lon, lat] as [number, number]);
          // Ensure the ring is closed for a valid GeoJSON Polygon.
          const first = ring[0];
          const last = ring[ring.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
          return {
            type: 'Feature' as const,
            geometry: { type: 'Polygon' as const, coordinates: [ring] },
            properties: {
              id: circle.id,
              name: circle.name,
              radius: circle.radius,
              centerLat: circle.center[0],
              centerLon: circle.center[1],
              hasResults: (circle.results && circle.results.length > 0),
              resultCount: circle.results ? circle.results.length : 0,
            },
          };
        }
        // Create a circle polygon approximation
        const center = circle.center; // [lat, lon]
        const radius = circle.radius;
        const points = 64; // Number of points to approximate the circle
        
        const coordinates: [number, number][] = [];
        for (let i = 0; i <= points; i++) {
          const angle = (i * 360) / points;
          const radians = (angle * Math.PI) / 180;
          
          // Simple approximation - for precise geodesic circles, a library like turf.js would be better
          const deltaLat = (radius / 111320) * Math.cos(radians); // ~111320 meters per degree latitude
          const deltaLon = (radius / (111320 * Math.cos((center[0] * Math.PI) / 180))) * Math.sin(radians);
          
          const lat = center[0] + deltaLat;
          const lon = center[1] + deltaLon;
          
          coordinates.push([lon, lat]); // GeoJSON uses [lon, lat]
        }
        
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Polygon' as const,
            coordinates: [coordinates]
          },
          properties: {
            id: circle.id,
            name: circle.name,
            radius: circle.radius,
            centerLat: circle.center[0],
            centerLon: circle.center[1],
            hasResults: (circle.results && circle.results.length > 0),
            resultCount: circle.results ? circle.results.length : 0
          }
        };
      });

    return { type: 'FeatureCollection' as const, features };
  }, [drawCircles]);

  // Generate GeoJSON for circle centers (for labels and interaction)
  const circleCentersGeoJSON = useMemo(() => {
    if (drawCircles.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = drawCircles
      .filter(circle => circle.visible)
      .map(circle => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [circle.center[1], circle.center[0]] // [lon, lat]
        },
        properties: {
          id: circle.id,
          name: circle.name,
          radius: circle.radius,
          hasResults: (circle.results && circle.results.length > 0),
          resultCount: circle.results ? circle.results.length : 0
        }
      }));

    return { type: 'FeatureCollection' as const, features };
  }, [drawCircles]);

  // Convert data points to GeoJSON
  const pointsGeoJSON = useMemo(() => {
    
    if (dataPoints.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = dataPoints.map(point => {
      const studentId = point.studentId || point.deviceId;
      const deviceType = (point.deviceType || 'phone').toLowerCase();
      
      // Determine if this is a co-traveler track point
      const isCoTravelerTrack = (point as any).isCoTraveler === true;
      
      // Use different colors for co-traveler tracks
      let color = studentColors[point.deviceId] || '#FF6B6B';
      let opacity = 0.8;
      
      if (isCoTravelerTrack) {
        // Prefer the live studentColors entry keyed by the cotraveler row key
        // (e.g. "Student-22 - Phone") so user-picked custom colors flow
        // through without needing to reload tracks. Fall back to the
        // baked-in trackColor (set at loadCoTravelerTracks time) and the
        // legacy orange default in that order.
        const ctKey = (point as any).coTravelerKey as string | undefined;
        color =
          (ctKey && studentColors[ctKey]) ||
          (point as any).trackColor ||
          '#ff9800';
        opacity = (point as any).trackOpacity || 0.7;
      }
      
      // Validate coordinates before creating feature
      if (!point.coordinates || !Array.isArray(point.coordinates) || point.coordinates.length < 2) {
        console.warn('⚠️ Skipping point with invalid coordinates:', point.id, point.coordinates);
        return null;
      }
      
      const lat = point.coordinates[0];
      const lng = point.coordinates[1];
      
      if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
        console.warn('⚠️ Skipping point with non-numeric coordinates:', point.id, { lat, lng });
        return null;
      }
      
      return {
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [lng, lat] // [lng, lat] for MapboxGL
        },
        properties: {
          id: point.id,
          studentId,
          deviceId: point.deviceId,
          deviceType,
          timestamp: point.timestamp.toISOString(),
          timeString: point.timestamp.toLocaleString(),
          color,
          opacity,
          accuracy: point.accuracy,
          activity: point.activity,
          isCoTravelerTrack: isCoTravelerTrack,
          trackType: (point as any).trackType || 'primary'
        }
      };
    }).filter((feature): feature is NonNullable<typeof feature> => feature !== null); // Remove null features from invalid coordinates

    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [dataPoints, enableClustering, showDataPoints, studentColors]);

  // Source key to force remount when clustering mode changes
  const pointsSourceKey = useMemo(() => (enableClustering ? 'points-source-clustered' : 'points-source-plain'), [enableClustering]);
  // Distinct source ids per mode: react-map-gl reuses a source by id during the
  // keyed remount, which would keep the old cluster setting.
  const pttSourceId = clusterPttMessages ? 'ptt-messages-clustered' : 'ptt-messages';
  // The bubble is an SDF image so it can be tinted per sender to match their track.
  const pttIconLayout = useMemo(() => ({
    'icon-image': 'ptt-message',
    'icon-size': ['case', ['==', ['get', 'active'], 1], 1.1, 0.75] as any,
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
  }), []);
  const pttIconPaint = useMemo(() => ({
    'icon-color': ['get', 'color'] as any,
    'icon-halo-color': '#ffffff',
    'icon-halo-width': ['case', ['==', ['get', 'active'], 1], 1.5, 1] as any,
    'icon-opacity': [
      'case',
      ['==', ['get', 'active'], 1], 1,
      ['==', ['get', 'inWindow'], 1], 0.9,
      0.3,
    ] as any,
  }), []);

  /** Sender → track colour, trying the same keys `studentColors` is built with. */
  const pttColorFor = useCallback((u: NonNullable<PathfinderMapGLProps['pttUtterances']>[number]): string => {
    const keys = [u.sender || '', u.entity_id || '', u.device_id || ''].filter(Boolean);
    for (const k of keys) {
      if (pttSenderColors && pttSenderColors[k]) return pttSenderColors[k];
    }
    return '#ffb300';
  }, [pttSenderColors]);

  const pttGeoJSON = useMemo(() => {
    const t = currentPlaybackTime?.getTime() ?? 0;
    const winStart = pttPlaybackWindow?.start.getTime() ?? -Infinity;
    const winEnd = pttPlaybackWindow?.end.getTime() ?? Infinity;
    const features = pttUtterances
      .filter((u) => u.latitude != null && u.longitude != null)
      .map((u) => {
        const start = u.started_at ? new Date(u.started_at).getTime() : 0;
        const end = u.ended_at ? new Date(u.ended_at).getTime() : start;
        const active = t > 0 && start <= t && t <= end ? 1 : 0;
        const inWindow = pttPlaybackWindow == null || (start <= winEnd && end >= winStart) ? 1 : 0;
        return {
          type: 'Feature' as const,
          properties: {
            ptt: true,
            id: u.id,
            active,
            inWindow,
            color: pttColorFor(u),
            sender: u.sender || u.entity_id || u.device_id,
            team: u.team || '',
            transcript: u.transcript || '(no transcript)',
            device_id: u.device_id,
            started_at: u.started_at || '',
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [u.longitude as number, u.latitude as number],
          },
        };
      });
    return { type: 'FeatureCollection' as const, features };
  }, [pttUtterances, currentPlaybackTime, pttPlaybackWindow, pttColorFor]);

  // Leader lines from each active transmission to the sender's track point at that moment,
  // so the bubble reads as "this person said this from here" even when GPS and PTT fixes differ.
  const pttLeaderGeoJSON = useMemo(() => {
    const t = currentPlaybackTime?.getTime() ?? 0;
    const features: Array<{ type: 'Feature'; properties: { color: string }; geometry: { type: 'LineString'; coordinates: [number, number][] } }> = [];
    if (t <= 0 || dataPoints.length === 0) return { type: 'FeatureCollection' as const, features };
    for (const u of pttUtterances) {
      if (u.latitude == null || u.longitude == null) continue;
      const start = u.started_at ? new Date(u.started_at).getTime() : 0;
      const end = u.ended_at ? new Date(u.ended_at).getTime() : start;
      if (!(start <= t && t <= end)) continue;
      const ent = u.entity_id || '';
      let best: PathfinderDataPoint | null = null;
      let bestDt = Infinity;
      for (const p of dataPoints) {
        if (p.studentId !== ent && p.deviceId !== u.device_id) continue;
        const dt = Math.abs(p.timestamp.getTime() - start);
        if (dt < bestDt) { bestDt = dt; best = p; }
      }
      // Only draw when the fix is within 5 minutes and not already on top of the bubble.
      if (!best || bestDt > 5 * 60_000) continue;
      const trackLngLat: [number, number] = [best.coordinates[1], best.coordinates[0]];
      const bubbleLngLat: [number, number] = [u.longitude as number, u.latitude as number];
      if (Math.abs(trackLngLat[0] - bubbleLngLat[0]) < 1e-6 && Math.abs(trackLngLat[1] - bubbleLngLat[1]) < 1e-6) continue;
      features.push({
        type: 'Feature',
        properties: { color: pttColorFor(u) },
        geometry: { type: 'LineString', coordinates: [bubbleLngLat, trackLngLat] },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [pttUtterances, currentPlaybackTime, dataPoints, pttColorFor]);

  // Helper function to calculate distance between two points (Haversine formula)
  const calculateDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }, []);

  // Convert routes to GeoJSON (with gap splitting when hideGaps is enabled)
  const routesGeoJSON = useMemo(() => {
    
    if (!showRouteLines || routes.length === 0) {
      return {
        type: 'FeatureCollection' as const,
        features: []
      };
    }

    const allFeatures: any[] = [];

    // Mapbox silently drops a LineString if ANY vertex is NaN / out of
    // bounds. Filter route coordinates here so a single bad point doesn't
    // erase the whole polyline. (The points layer already validates
    // per-feature, which is why clusters render even when the line doesn't.)
    const isValidLngLat = (c: any): boolean => {
      if (!c || !Array.isArray(c) || c.length < 2) return false;
      const lng = c[0];
      const lat = c[1];
      if (typeof lng !== 'number' || typeof lat !== 'number') return false;
      if (!isFinite(lng) || !isFinite(lat)) return false;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
      if (lat === 0 && lng === 0) return false;
      return true;
    };
    
    // Gap / implausible thresholds from org settings + session overrides
    // (defaults: 30 min, 500 m, 160 km/h ~100 mph). Admins can raise the
    // speed toward the old hard-coded 250 km/h via Settings → Pathfinder.
    const MAX_TIME_GAP_MS = Math.max(1, gapMaxTimeMinutes) * 60 * 1000;
    const MAX_DISTANCE_M = Math.max(1, gapMaxDistanceM);
    const MAX_PLAUSIBLE_HOP_SPEED_KMH = Math.max(1, implausibleMaxSpeedKmh);

    routes.forEach(route => {
      // Determine if this is a co-traveler route
      const isCoTravelerRoute = (route as any).isCoTravelerRoute === true;
      
      // Use different styling for co-traveler routes
      let color = studentColors[route.deviceId] || studentColors[route.studentId] || '#1976d2';
      let opacity = 0.7;
      // Thin tracks so dense AdTech / multi-device PoL stays readable.
      let width = 1.5;
      
      if (isCoTravelerRoute) {
        // Prefer the live studentColors entry keyed by the cotraveler row
        // key so user-picked custom colors flow through without reloading.
        const ctKey = (route as any).coTravelerKey as string | undefined;
        color =
          (ctKey && studentColors[ctKey]) ||
          (route as any).routeColor ||
          '#ff9800';
        opacity = (route as any).routeOpacity || 0.7;
        width = (route as any).routeWidth || 1.5;
      }
      
      const deviceType = (route.deviceType || 'phone').toLowerCase();
      
      const baseProperties = {
        studentId: route.studentId,
        deviceId: route.deviceId,
        deviceType,
        color,
        opacity,
        width,
        isCoTravelerRoute: isCoTravelerRoute,
        routeType: (route as any).routeType || 'primary'
      };

      // If we don't have timestamped per-point data, fall back to the
      // simple "one LineString from coordinates" path. We can't apply the
      // phantom-hop filter without timestamps, so this is the unavoidable
      // best-effort case (older co-traveler payloads sometimes lack it).
      if (!route.points || route.points.length < 2) {
        const lineCoords = route.coordinates
          .map(coord => [coord[1], coord[0]] as [number, number])
          .filter(isValidLngLat);
        if (lineCoords.length < 2) return;
        allFeatures.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: lineCoords
          },
          properties: {
            ...baseProperties,
            startTime: route.startTime.toISOString(),
            endTime: route.endTime.toISOString(),
          }
        });
        return;
      }

      // Walk points to detect gaps. Implausible hops and GPS Gaps are
      // independently toggled (org defaults + session overrides).
      const segments: [number, number][][] = [];
      let currentSegment: [number, number][] = [];
      let prevValidPoint: typeof route.points[number] | null = null;

      for (let i = 0; i < route.points.length; i++) {
        const point = route.points[i];
        const coord: [number, number] = [point.coordinates[1], point.coordinates[0]]; // [lng, lat]

        // Skip points whose vertex would corrupt the LineString.
        if (!isValidLngLat(coord)) continue;

        if (prevValidPoint === null) {
          currentSegment.push(coord);
          prevValidPoint = point;
          continue;
        }

        const timeDiffMs = point.timestamp.getTime() - prevValidPoint.timestamp.getTime();
        const distanceM = calculateDistance(
          prevValidPoint.coordinates[0], prevValidPoint.coordinates[1],
          point.coordinates[0], point.coordinates[1]
        );

        // Implied speed = distance / time. Floor the dt at 1s so a
        // pair of points with identical timestamps doesn't divide by
        // zero and produce Infinity (which would correctly flag, but
        // is fragile to maintain).
        const dtHours = Math.max(1 / 3600, timeDiffMs / 3_600_000);
        const impliedSpeedKmh = (distanceM / 1000) / dtHours;

        const isPhantomHop =
          hideImplausibleMovement && impliedSpeedKmh > MAX_PLAUSIBLE_HOP_SPEED_KMH;
        const isUserHiddenGap =
          hideGaps && (timeDiffMs > MAX_TIME_GAP_MS || distanceM > MAX_DISTANCE_M);
        const isGap = isPhantomHop || isUserHiddenGap;

        if (isGap) {
          if (currentSegment.length >= 2) {
            segments.push(currentSegment);
          }
          currentSegment = [coord];
        } else {
          currentSegment.push(coord);
        }
        prevValidPoint = point;
      }

      // Don't forget the last segment
      if (currentSegment.length >= 2) {
        segments.push(currentSegment);
      }

      // Create features for each segment
      segments.forEach((segment, idx) => {
        allFeatures.push({
          type: 'Feature' as const,
          geometry: {
            type: 'LineString' as const,
            coordinates: segment
          },
          properties: {
            ...baseProperties,
            startTime: route.startTime.toISOString(),
            endTime: route.endTime.toISOString(),
            segmentIndex: idx
          }
        });
      });
    });

    return {
      type: 'FeatureCollection' as const,
      features: allFeatures
    };
  }, [
    routes,
    showRouteLines,
    studentColors,
    hideGaps,
    hideImplausibleMovement,
    implausibleMaxSpeedKmh,
    gapMaxTimeMinutes,
    gapMaxDistanceM,
    calculateDistance,
  ]);

  // Create point features at the leading edge of each route for direction arrows
  // Uses route.points sorted by timestamp to find the true leading edge
  const routeHeadsGeoJSON = useMemo(() => {
    if (!showRouteLines || routes.length === 0 || !showDirectionArrows) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const toRad = (d: number) => (d * Math.PI) / 180;
    const toDeg = (r: number) => (r * 180) / Math.PI;

    const features = routes
      .filter(r => r.points && r.points.length >= 2)
      .map((r) => {
        // Sort points by timestamp to find the true leading edge (newest point)
        const sortedPoints = [...r.points]
          .filter(p => p.coordinates && p.timestamp)
          .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
        if (sortedPoints.length < 2) {
          return null;
        }
        
        // The leading edge is the point with the LATEST timestamp
        const leadingPoint = sortedPoints[sortedPoints.length - 1];
        
        // For bearing, use overall route direction (from oldest to newest) for stability
        // This represents the true direction of travel, not momentary GPS jitter at the end
        const prevPoint = sortedPoints[0]; // Use the OLDEST point for overall direction
        
        const last = leadingPoint.coordinates; // [lat, lon]
        const prev = prevPoint.coordinates; // [lat, lon]
        
        if (!last || !prev) return null;
        
        // Calculate bearing from prev to last (direction of travel)
        const y = Math.sin(toRad(last[1] - prev[1])) * Math.cos(toRad(last[0]));
        const x = Math.cos(toRad(prev[0])) * Math.sin(toRad(last[0])) -
                  Math.sin(toRad(prev[0])) * Math.cos(toRad(last[0])) * Math.cos(toRad(last[1] - prev[1]));
        let brng = toDeg(Math.atan2(y, x));
        // Normalize to 0-360 range
        brng = (brng + 360) % 360;
        
        // Prefer cotraveler row key when present so direction arrows pick
        // up live custom colors alongside the route line.
        const ctKey = (r as any).coTravelerKey as string | undefined;
        const color =
          (ctKey && studentColors[ctKey]) ||
          studentColors[r.deviceId] ||
          '#FFFFFF';
        
        return {
          type: 'Feature' as const,
          geometry: {
            type: 'Point' as const,
            coordinates: [last[1], last[0]] // lng, lat for GeoJSON
          },
          properties: {
            bearing: brng,
            color,
            deviceId: r.deviceId,
            studentId: r.studentId
          }
        };
      }).filter((f): f is NonNullable<typeof f> => f !== null);
    return { type: 'FeatureCollection' as const, features };
  }, [routes, showRouteLines, showDirectionArrows, studentColors]);

  // One entity-number label per device at its latest position. Circles and
  // direction arrows stay as they are; the number sits above them the way
  // COP stamps the designator on a pin.
  const entityNumberGeoJSON = useMemo(() => {
    if (!showDataPoints && !showRouteLines) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    type Head = { lng: number; lat: number; time: number; label: string };
    // `Map` in this file is the Mapbox component, so keep a plain record.
    const heads: Record<string, Head> = {};

    const consider = (
      key: string,
      labelSource: string | undefined,
      lng: number,
      lat: number,
      time: number,
    ) => {
      const label = entityNumberDesignator(labelSource);
      if (!label || !Number.isFinite(lng) || !Number.isFinite(lat)) return;
      const prev = heads[key];
      if (!prev || time >= prev.time) {
        heads[key] = { lng, lat, time, label };
      }
    };

    if (routes.length > 0) {
      routes.forEach((route) => {
        const deviceType = (route.deviceType || 'phone').toLowerCase();
        const key = `${route.studentId || route.deviceId}|${deviceType}`;
        if (route.points && route.points.length > 0) {
          const sorted = [...route.points]
            .filter((p) => p.coordinates && p.coordinates.length >= 2)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          const latest = sorted[sorted.length - 1];
          if (!latest?.coordinates) return;
          consider(
            key,
            route.studentId || route.deviceId,
            latest.coordinates[1],
            latest.coordinates[0],
            latest.timestamp ? new Date(latest.timestamp).getTime() : 0,
          );
          return;
        }
        const last = route.coordinates?.[route.coordinates.length - 1];
        if (!last || last.length < 2) return;
        consider(key, route.studentId || route.deviceId, last[1], last[0], 0);
      });
    } else {
      dataPoints.forEach((point) => {
        if (!point.coordinates || point.coordinates.length < 2) return;
        const deviceType = (point.deviceType || 'phone').toLowerCase();
        const studentId = point.studentId || point.deviceId;
        consider(
          `${studentId}|${deviceType}`,
          studentId,
          point.coordinates[1],
          point.coordinates[0],
          point.timestamp ? point.timestamp.getTime() : 0,
        );
      });
    }

    return {
      type: 'FeatureCollection' as const,
      features: Object.values(heads).map((head) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [head.lng, head.lat] },
        properties: { label: head.label },
      })),
    };
  }, [routes, dataPoints, showDataPoints, showRouteLines]);

  // Position of the SRP closest to the selected student using the positions
  // currently on the map. Each track is sampled at its latest fix inside the
  // slider window (the latest point loaded for that view). Moving the slider
  // changes the window, so the ring moves to whichever SRP is closest then.
  const closestSrpHaloGeoJSON = useMemo(() => {
    const empty = { type: 'FeatureCollection' as const, features: [] as any[] };
    if (!highlightClosestSrp || !selectedStudentId) return empty;

    const entityOf = (id?: string) => (id || '').split(' - ')[0].trim().toUpperCase();
    const selectedEntity = entityOf(selectedStudentId);
    if (!selectedEntity) return empty;

    const windowStartMs = playbackWindow?.start ? new Date(playbackWindow.start).getTime() : -Infinity;
    const windowEndMs = playbackWindow?.end ? new Date(playbackWindow.end).getTime() : Infinity;

    type Pos = { lat: number; lng: number; time: number };
    const studentByDevice: Record<string, Pos> = {};
    const srpByDevice: Record<string, Pos & { entity: string }> = {};

    const consider = (
      entityRaw: string | undefined,
      deviceId: string | undefined,
      coordinates: [number, number] | undefined,
      timestamp: Date | string | undefined,
    ) => {
      if (!coordinates || coordinates.length < 2) return;
      const [lat, lng] = coordinates;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const entity = entityOf(entityRaw || deviceId);
      if (!entity) return;
      const time = timestamp ? new Date(timestamp).getTime() : 0;
      if (!Number.isFinite(time) || time < windowStartMs || time > windowEndMs) return;
      const key = deviceId || entity;
      const next = { lat, lng, time };
      if (entity.startsWith('SRP-')) {
        const prev = srpByDevice[key];
        if (!prev || time >= prev.time) srpByDevice[key] = { ...next, entity };
      } else if (entity === selectedEntity) {
        const prev = studentByDevice[key];
        if (!prev || time >= prev.time) studentByDevice[key] = next;
      }
    };

    for (const route of routes) {
      const points = route.points || [];
      if (points.length > 0) {
        for (const point of points) {
          consider(route.studentId || point.studentId, route.deviceId || point.deviceId, point.coordinates, point.timestamp);
        }
      } else {
        const last = route.coordinates?.[route.coordinates.length - 1];
        consider(route.studentId, route.deviceId, last, route.endTime);
      }
    }
    if (Object.keys(studentByDevice).length === 0 || Object.keys(srpByDevice).length === 0) {
      for (const point of dataPoints) {
        consider(point.studentId, point.deviceId, point.coordinates, point.timestamp);
      }
    }

    const students = Object.values(studentByDevice);
    const srps = Object.values(srpByDevice);
    if (students.length === 0 || srps.length === 0) return empty;

    let best: (typeof srps)[number] | null = null;
    let bestDist = Infinity;
    for (const srp of srps) {
      for (const student of students) {
        const d = calculateDistance(student.lat, student.lng, srp.lat, srp.lng);
        if (d < bestDist) {
          bestDist = d;
          best = srp;
        }
      }
    }
    if (!best) return empty;

    return {
      type: 'FeatureCollection' as const,
      features: [{
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [best.lng, best.lat] },
        properties: { label: best.entity },
      }],
    };
  }, [highlightClosestSrp, selectedStudentId, routes, dataPoints, playbackWindow, calculateDistance]);

  // Co-traveler connections GeoJSON
  const coTravelerConnectionsGeoJSON = useMemo(() => {
    if (!_showCoTravelerPoints || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const connectionFeatures = coTravelerResults.flatMap(result => {
      return result.encounters.map(encounter => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [encounter.location[1], encounter.location[0]] // [lng, lat]
        },
        properties: {
          type: 'co-traveler-encounter',
          primaryDevice: result.primaryDevice,
          coTravelerDevice: result.coTravelerDevice,
          timestamp: encounter.timestamp.toISOString(),
          duration: encounter.duration,
          proximity: encounter.proximity,
          confidence: encounter.confidence,
          activity: encounter.activity,
          locationName: encounter.locationName
        }
      }));
    });

    return { type: 'FeatureCollection' as const, features: connectionFeatures };
  }, [coTravelerResults, _showCoTravelerPoints]);

  // Co-traveler connection lines GeoJSON
  const coTravelerLinesGeoJSON = useMemo(() => {
    if (!_showCoTravelerLines || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const lineFeatures = coTravelerResults.flatMap(result => {
      // Group encounters by time windows to create connection lines
      const timeWindows: { [key: string]: CoTravelerEncounter[] } = {};
      
      result.encounters.forEach(encounter => {
        const timeKey = Math.floor(encounter.timestamp.getTime() / (5 * 60 * 1000)).toString(); // 5-minute windows
        if (!timeWindows[timeKey]) {
          timeWindows[timeKey] = [];
        }
        timeWindows[timeKey].push(encounter);
      });

      return Object.values(timeWindows)
        .filter(encounters => encounters.length >= 2)
        .map(encounters => {
          // Create line connecting encounter locations
          const coordinates = encounters.map(e => [e.location[1], e.location[0]]); // [lng, lat]
          const avgConfidence = encounters.reduce((sum, e) => sum + e.confidence, 0) / encounters.length;
          
          return {
            type: 'Feature' as const,
            geometry: {
              type: 'LineString' as const,
              coordinates
            },
            properties: {
              type: 'co-traveler-connection',
              primaryDevice: result.primaryDevice,
              coTravelerDevice: result.coTravelerDevice,
              encounters: encounters.length,
              confidence: avgConfidence,
              totalDuration: encounters.reduce((sum, e) => sum + e.duration, 0)
            }
          };
        });
    });

    return { type: 'FeatureCollection' as const, features: lineFeatures };
  }, [coTravelerResults, _showCoTravelerLines]);

  // Co-traveler heatmap GeoJSON - shows encounter density
  const coTravelerHeatmapGeoJSON = useMemo(() => {
    if (!_showCoTravelerHeatmap || coTravelerResults.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    // Create heatmap points from all co-traveler encounters
    const heatmapFeatures = coTravelerResults.flatMap(result => 
      result.encounters.map(encounter => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [encounter.location[1], encounter.location[0]] // [lng, lat]
        },
        properties: {
          type: 'co-traveler-heatmap',
          primaryDevice: result.primaryDevice,
          coTravelerDevice: result.coTravelerDevice,
          confidence: encounter.confidence,
          duration: encounter.duration,
          timestamp: encounter.timestamp.toISOString(),
          // Weight for heatmap intensity based on confidence and duration
          weight: encounter.confidence * (encounter.duration / 60) // Convert duration to minutes
        }
      }))
    );

    return { type: 'FeatureCollection' as const, features: heatmapFeatures };
  }, [coTravelerResults, _showCoTravelerHeatmap]);

  // Enhanced heatmap data processing with performance optimization and time filtering
  const routePointsGeoJSON = useMemo(() => {
    if (routes.length === 0) return { type: 'FeatureCollection' as const, features: [] };
    
    // Performance optimization: Sample points for large datasets
    const maxPointsPerRoute = 100; // Limit points per route for performance
    const features = routes.flatMap(route => {
      const coords = route.coordinates || [];
      if (coords.length === 0) return [];
      
      // Time-based filtering if timeRange is provided
      const routeStartTime = new Date(route.startTime).getTime();
      const routeEndTime = new Date(route.endTime).getTime();
      
      if (timeRange) {
        const timeRangeStart = timeRange.start.getTime();
        const timeRangeEnd = timeRange.end.getTime();
        
        // Skip route if it doesn't overlap with time range
        if (routeEndTime < timeRangeStart || routeStartTime > timeRangeEnd) {
          return [];
        }
      }
      
      // Sample points if route is too long
      const sampledCoords = coords.length > maxPointsPerRoute 
        ? coords.filter((_, index) => index % Math.ceil(coords.length / maxPointsPerRoute) === 0)
        : coords;
      
      return sampledCoords.map(coord => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [coord[1], coord[0]] },
        properties: { 
          deviceId: route.deviceId,
          deviceType: route.deviceType,
          weight: 1, // Base weight for heatmap intensity
          timestamp: routeStartTime // Use route start time for reference
        }
      }));
    });
    
    return { type: 'FeatureCollection' as const, features };
  }, [routes, timeRange]);

  // Suppress "phantom" dwells produced by GPS positioning fallback.
  //
  // When indoor GPS is unavailable - or the device is inside a GPS-denied
  // zone like MacDill AFB - the OS falls back to Wi-Fi BSSID lookup and
  // cell-tower triangulation. A stationary phone in Tampa Bay then often
  // produces three time-overlapping "dwells" at TPA, PIE, and MacDill
  // simultaneously, because the positioning service oscillates between
  // those Wi-Fi-dense anchors. A real phone can't be in three places at
  // once.
  //
  // For each device, this pass groups dwells whose time windows overlap
  // AND whose centroids are > 2 km apart, then keeps only the
  // longest-duration dwell from each conflict cluster. Result: instead
  // of three airport circles, the map shows the one that absorbed the
  // most signal — usually the closest to the real (unreported) location.
  //
  // This is display-only suppression; the underlying device_dwells rows
  // are untouched in the DB, so a backend cleanup migration could
  // replace this filter later if/when we want to fix the data at rest.
  const PHANTOM_DWELL_MIN_DISTANCE_KM = 2;
  const filteredDwellLocations = useMemo(() => {
    if (dwellLocations.length === 0) return dwellLocations;

    // Group by device first - a phone can be in two places when there
    // are two different phones. Use deviceid (the canonical identifier
    // shared with device_tracks and the trigger function); fall back to
    // entity_id when deviceid is missing (very old records). Using a
    // plain object as a dictionary because the `Map` global is shadowed
    // in this file by the react-map-gl Map component import.
    const byDevice: Record<string, any[]> = {};
    for (const d of dwellLocations) {
      const dAny = d as any;
      const key = (dAny.deviceid || dAny.device_id || dAny.entity_id || 'unknown') as string;
      if (byDevice[key]) byDevice[key].push(d);
      else byDevice[key] = [d];
    }

    // Haversine distance in km. Local helper because `calculateDistance`
    // earlier returns meters and a per-call shape that's awkward here.
    const haversineKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
      const toRad = (deg: number) => (deg * Math.PI) / 180;
      const R = 6371;
      const dLat = toRad(lat2 - lat1);
      const dLon = toRad(lon2 - lon1);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
      return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const kept: any[] = [];
    let suppressedCount = 0;
    for (const deviceKey of Object.keys(byDevice)) {
      const dwells = byDevice[deviceKey];
      const n = dwells.length;
      // Union-Find: build connected components where two dwells are in
      // the same component iff they're mutually exclusive (time overlap
      // + far apart).
      const parent = Array.from({ length: n }, (_, i) => i);
      const find = (x: number): number => {
        let r = x;
        while (parent[r] !== r) r = parent[r];
        while (parent[x] !== r) {
          const next = parent[x];
          parent[x] = r;
          x = next;
        }
        return r;
      };
      const union = (a: number, b: number) => {
        const ra = find(a);
        const rb = find(b);
        if (ra !== rb) parent[ra] = rb;
      };

      const starts = dwells.map((d) => new Date(d.start_time).getTime());
      const ends = dwells.map((d) => new Date(d.end_time).getTime());

      for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
          // Time overlap: [start_i, end_i] intersects [start_j, end_j]
          if (starts[i] > ends[j] || starts[j] > ends[i]) continue;
          // Distance test
          const km = haversineKm(
            dwells[i].latitude, dwells[i].longitude,
            dwells[j].latitude, dwells[j].longitude,
          );
          if (km <= PHANTOM_DWELL_MIN_DISTANCE_KM) continue;
          union(i, j);
        }
      }

      // Group by component root, keep longest-duration dwell per group.
      // Using a plain Record<number, ...> for the same Map-shadowing
      // reason described above.
      const groups: Record<number, number[]> = {};
      for (let i = 0; i < n; i++) {
        const r = find(i);
        if (groups[r]) groups[r].push(i);
        else groups[r] = [i];
      }
      for (const indices of Object.values(groups)) {
        if (indices.length === 1) {
          kept.push(dwells[indices[0]]);
          continue;
        }
        let bestIdx = indices[0];
        let bestDur = dwells[bestIdx].duration_seconds ?? (ends[bestIdx] - starts[bestIdx]) / 1000;
        for (let k = 1; k < indices.length; k++) {
          const i = indices[k];
          const dur = dwells[i].duration_seconds ?? (ends[i] - starts[i]) / 1000;
          if (dur > bestDur) {
            bestIdx = i;
            bestDur = dur;
          }
        }
        kept.push(dwells[bestIdx]);
        suppressedCount += indices.length - 1;
      }
    }

    if (suppressedCount > 0) {
      // Quiet debug log so analysts can see if the filter is doing work.
      // The map itself won't badge this until we add a footer indicator.
      // eslint-disable-next-line no-console
      console.debug(
        `[PathfinderMap] Suppressed ${suppressedCount} phantom dwell(s) (overlapping in time + >${PHANTOM_DWELL_MIN_DISTANCE_KM}km apart)`,
      );
    }
    return kept;
  }, [dwellLocations]);

  // Dwell insets: zoomed-in mini maps pinned beside chosen dwells so one
  // screenshot carries the overview plus close-ups. Created only on demand
  // (popup action or toolbar menu) - nothing is fetched for unpinned dwells.
  const [dwellInsets, setDwellInsets] = useState<DwellInset[]>([]);
  const [dwellInsetsVisible, setDwellInsetsVisible] = useState(true);
  const [dwellInsetMenuAnchor, setDwellInsetMenuAnchor] = useState<HTMLElement | null>(null);
  const dwellInsetKeys = useMemo(() => new Set(dwellInsets.map((i) => i.key)), [dwellInsets]);
  const describeDwellForInset = useCallback((d: PathfinderMapDwellLocation): Omit<DwellInset, 'dx' | 'dy'> => {
    const s = Number(d.duration_seconds || 0);
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const dur = hh > 0 ? `${hh}h ${mm}m` : mm > 0 ? `${mm}m` : `${Math.floor(s)}s`;
    const start = d.start_time ? new Date(d.start_time) : null;
    const when = start && !Number.isNaN(start.getTime())
      ? start.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : '';
    return {
      key: dwellInsetKey(d.id, d.longitude, d.latitude),
      lon: d.longitude,
      lat: d.latitude,
      title: d.place_name || `${d.latitude.toFixed(5)}, ${d.longitude.toFixed(5)}`,
      subtitle: [d.entity_id, dur, when].filter(Boolean).join(' · '),
    };
  }, []);
  const toggleDwellInset = useCallback((d: PathfinderMapDwellLocation) => {
    const desc = describeDwellForInset(d);
    setDwellInsets((prev) => {
      if (prev.some((i) => i.key === desc.key)) return prev.filter((i) => i.key !== desc.key);
      return [...prev, { ...desc, ...defaultInsetOffset(prev.length) }];
    });
    setDwellInsetsVisible(true);
  }, [describeDwellForInset]);
  /** Cap for "inset all" so the map stays readable; longest dwells first. */
  const DWELL_INSET_ALL_LIMIT = 8;
  const CAMERA_CALLOUT_ALL_LIMIT = 12;
  const addInsetsForShownDwells = useCallback(() => {
    const ranked = [...filteredDwellLocations].sort((a, b) => (b.duration_seconds || 0) - (a.duration_seconds || 0));
    setDwellInsets((prev) => {
      const next = [...prev];
      for (const d of ranked) {
        if (next.length >= DWELL_INSET_ALL_LIMIT) break;
        const desc = describeDwellForInset(d);
        if (next.some((i) => i.key === desc.key)) continue;
        next.push({ ...desc, ...defaultInsetOffset(next.length) });
      }
      return next;
    });
    setDwellInsetsVisible(true);
  }, [filteredDwellLocations, describeDwellForInset]);
  const moveDwellInset = useCallback((key: string, dx: number, dy: number) => {
    setDwellInsets((prev) => prev.map((i) => (i.key === key ? { ...i, dx, dy } : i)));
  }, []);
  const resizeDwellInset = useCallback((key: string, w: number, h: number) => {
    setDwellInsets((prev) => prev.map((i) => (i.key === key ? { ...i, w, h } : i)));
  }, []);
  const zoomDwellInset = useCallback((key: string, zoom: number) => {
    setDwellInsets((prev) => prev.map((i) => (i.key === key ? { ...i, zoom } : i)));
  }, []);
  const toggleDwellInsetImagery = useCallback((key: string) => {
    setDwellInsets((prev) => prev.map((i) => (i.key === key ? { ...i, imagery: !i.imagery } : i)));
  }, []);
  const allInsetsImagery = dwellInsets.length > 0 && dwellInsets.every((i) => i.imagery);
  const setAllInsetsImagery = useCallback((imagery: boolean) => {
    setDwellInsets((prev) => prev.map((i) => ({ ...i, imagery })));
  }, []);
  const removeDwellInset = useCallback((key: string) => {
    setDwellInsets((prev) => prev.filter((i) => i.key !== key));
  }, []);
  // Drop insets whose dwell is no longer loaded (new query / cleared results).
  useEffect(() => {
    if (dwellInsets.length === 0) return;
    const live = new Set(filteredDwellLocations.map((d) => dwellInsetKey(d.id, d.longitude, d.latitude)));
    if (dwellInsets.every((i) => live.has(i.key))) return;
    setDwellInsets((prev) => prev.filter((i) => live.has(i.key)));
  }, [filteredDwellLocations, dwellInsets]);

  // Route sequence: cameras passed + dwells along the loaded track, in order.
  // Only computed while the overlay is open; the badges layer and the panel
  // both read from the same groups so the numbers always agree.
  const [routeSequenceOpen, setRouteSequenceOpen] = useState(false);
  const [routeSequenceBadgesOn, setRouteSequenceBadgesOn] = useState(true);
  const preferredTimezoneSetting = useSettingsStore((s) => s.settings.temporal.preferredTimezone);
  const routeSequenceTz = useMemo(() => getPreferredTimeZone(preferredTimezoneSetting), [preferredTimezoneSetting]);
  // Also needed while the cameras strip is open so its tiles follow route order.
  // ... and while the Story HUD is open, so the AI summary knows which cameras were passed.
  const routeSequenceNeeded = routeSequenceOpen || (camerasInViewOpen && showCameraLocations) || storyHudOpen;
  const routeSequencePoints = useMemo<RouteSequencePoint[]>(() => {
    if (!routeSequenceNeeded || !(showCameraLocations || storyHudOpen)) return [];
    const out: RouteSequencePoint[] = [];
    for (const p of dataPoints) {
      const c = p.coordinates; // [lat, lon]
      if (!Array.isArray(c) || c.length < 2) continue;
      const lat = Number(c[0]);
      const lon = Number(c[1]);
      const t = p.timestamp instanceof Date ? p.timestamp : new Date(p.timestamp as unknown as string);
      if (!Number.isFinite(lat) || !Number.isFinite(lon) || Number.isNaN(t.getTime())) continue;
      out.push({ lon, lat, time: t, entityId: p.studentId || p.deviceId, deviceId: p.deviceId, deviceType: p.deviceType });
    }
    return out;
  }, [routeSequenceNeeded, showCameraLocations, storyHudOpen, dataPoints]);
  const routeSequenceDwells = useMemo<RouteSequenceDwell[]>(() => {
    if (!routeSequenceNeeded || !showDwells) return [];
    return (filteredDwellLocations as PathfinderMapDwellLocation[]).map((d) => ({
      id: d.id,
      entityId: d.entity_id,
      deviceType: d.device_type,
      lon: d.longitude,
      lat: d.latitude,
      start: new Date(d.start_time),
      end: new Date(d.end_time),
      durationS: Number(d.duration_seconds || 0),
      placeName: d.place_name,
      address: d.place_address,
    }));
  }, [routeSequenceNeeded, showDwells, filteredDwellLocations]);
  const routeSequenceGroups = useMemo(
    () =>
      routeSequenceNeeded
        ? buildRouteSequence({
            points: routeSequencePoints,
            cameras: (showCameraLocations || storyHudOpen) ? cameraLayerData : null,
            radiusM: cameraNearTracks.radiusM,
            dwells: routeSequenceDwells,
          })
        : [],
    [routeSequenceNeeded, routeSequencePoints, showCameraLocations, storyHudOpen, cameraLayerData, cameraNearTracks.radiusM, routeSequenceDwells]
  );
  // Route-sequence badges, stacked where they collide on screen ("7–8"). Relative
  // pixel distances only change with zoom (and the 3D pitch), so re-stack on those.
  const routeSequenceBadgeData = useMemo(() => {
    const raw = routeSequenceBadges(routeSequenceGroups);
    const m = mapRef.current?.getMap();
    return stackBadges(
      raw.features.map((f) => ({
        lngLat: f.geometry.coordinates as [number, number],
        seq: Number(f.properties.seq),
        kind: String(f.properties.kind),
        color: String(f.properties.color),
        extra: { entity: f.properties.entity, label: f.properties.label },
      })),
      (ll) => (m ? m.project(ll) : null),
      { rank: (k) => (k === 'dwell' ? 1 : 0), anchorKinds: new Set(['dwell']) },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSequenceGroups, viewport.zoom, is3DMode]);
  // First pass per camera (keyed like callouts) → orders the cameras strip by route time.
  // (`Map` is react-map-gl's component in this file, so build a plain record first.)
  const cameraSequenceRank = useMemo<ReadonlyMap<string, { seq: number; time: number }>>(() => {
    const rank: Record<string, { seq: number; time: number }> = {};
    for (const g of routeSequenceGroups) {
      for (const e of g.entries) {
        if (e.kind !== 'camera' || !e.cameraId) continue;
        const key = `${e.cameraId}@${e.lon.toFixed(6)},${e.lat.toFixed(6)}`;
        const prev = rank[key];
        if (!prev || e.time.getTime() < prev.time) rank[key] = { seq: e.seq, time: e.time.getTime() };
      }
    }
    return new (globalThis.Map as MapConstructor)(Object.entries(rank));
  }, [routeSequenceGroups]);
  const sequenceBadgesShowing = routeSequenceOpen && routeSequenceBadgesOn && routeSequenceBadgeData.features.length > 0;
  // Numbers stay above dwell circles / cameras no matter what gets toggled on later.
  const getMapForLayers = useCallback(() => mapRef.current?.getMap(), []);
  useKeepLayersOnTop(getMapForLayers, ROUTE_SEQUENCE_BADGE_LAYERS, sequenceBadgesShowing);
  // Story HUD and the route-sequence table are two numbering systems for the same
  // thing; only one may be open at a time (the Story HUD wins).
  useEffect(() => {
    if (storyHudOpen) setRouteSequenceOpen(false);
  }, [storyHudOpen]);
  // Open (and restore) the Story timeline - used by the camera tool so Street View
  // callouts and the numbered sequence show up together.
  const openStoryTimeline = useCallback(() => {
    if (!isFullscreen) return;
    setRouteSequenceOpen(false);
    setHudPanelOpen('story', true);
    setHudPanelMinimized('story', false);
  }, [isFullscreen, setHudPanelOpen, setHudPanelMinimized]);
  // Cameras the loaded track passed, in route order, shaped like strip items so
  // they can be pinned as Street View callouts from the toolbar.
  const passedCameraItems = useMemo<CameraInView[]>(() => {
    if (routeSequenceGroups.length === 0) return [];
    const byId: Record<string, CameraLocationProperties> = {};
    for (const f of cameraLayerData.features) {
      const id = f.properties?.camera_id;
      if (id) byId[String(id)] = f.properties;
    }
    const seen = new Set<string>();
    const out: CameraInView[] = [];
    for (const g of routeSequenceGroups) {
      for (const e of g.entries) {
        if (e.kind !== 'camera' || !e.cameraId) continue;
        const camera = byId[e.cameraId];
        if (!camera) continue;
        const item: CameraInView = { camera, lon: e.lon, lat: e.lat, heading: cameraHeading(camera), distanceM: e.distanceM ?? 0 };
        const key = cameraCalloutKey(item);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(item);
      }
    }
    return out;
  }, [routeSequenceGroups, cameraLayerData]);
  const pinCalloutsForPassedCameras = useCallback(() => {
    setCameraCallouts((prev) => {
      const have = new Set(prev.map((c) => c.key));
      const next = [...prev];
      for (const item of passedCameraItems) {
        if (next.length >= CAMERA_CALLOUT_ALL_LIMIT) break;
        const key = cameraCalloutKey(item);
        if (have.has(key)) continue;
        have.add(key);
        next.push({ key, item, ...defaultCalloutOffset(next.length) });
      }
      return next;
    });
    setCameraCalloutsVisible(true);
  }, [passedCameraItems]);
  // Publish cameras passed to the Story AI summary (HUD or left panel) so it can count / cite them.
  // Available whenever the Story HUD is open or the camera layer is on (route sequence is computed then).
  useEffect(() => {
    if (!storyHudOpen && !showCameraLocations) {
      setStoryCameras(null);
      return;
    }
    const passed: StoryCameraContext['passed'] = [];
    for (const g of routeSequenceGroups) {
      for (const e of g.entries) {
        if (e.kind !== 'camera') continue;
        passed.push({
          seq: e.seq,
          entityId: e.entityId,
          cameraId: e.cameraId,
          label: e.label,
          category: e.categoryLabel ?? e.category ?? null,
          time: e.time.toISOString(),
          endTime: e.endTime.toISOString(),
          lat: e.lat,
          lng: e.lon,
          distanceM: e.distanceM,
          hits: e.hits,
        });
        if (passed.length >= 150) break;
      }
      if (passed.length >= 150) break;
    }
    setStoryCameras({
      layerOn: showCameraLocations,
      loadedCount: cameraTotalCount || cameraLocationsData?.features?.length || 0,
      nearCount: cameraLayerData?.features?.length ?? 0,
      passedCount: passed.length,
      radiusM: cameraNearTracks.radiusM,
      passed,
    });
  }, [storyHudOpen, showCameraLocations, routeSequenceGroups, cameraLayerData, cameraLocationsData, cameraTotalCount, cameraNearTracks.radiusM, setStoryCameras]);
  // Story HUD numbers also sit on dwell locations; insets / callouts use the hollow ring anchor so they stay readable.
  const storyBadgesShowing = useStoryBadgesShowing();
  const badgesShowing = sequenceBadgesShowing || storyBadgesShowing;
  const routeSequenceAvailable = dataPoints.length > 0 && (showCameraLocations || (showDwells && filteredDwellLocations.length > 0));
  const flyToSequenceEntry = useCallback((e: RouteSequenceEntry) => {
    mapRef.current?.flyTo({ center: [e.lon, e.lat], zoom: Math.max(mapRef.current.getZoom(), 16), duration: 600 });
  }, []);

  // Generate GeoJSON for dwell locations (transparent red circles)
  const dwellsGeoJSON = useMemo(() => {

    if (!showDwells) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    if (filteredDwellLocations.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features = filteredDwellLocations.map((dwell, _index) => {
      // Calculate radius in meters based on duration (longer dwells = larger circles)
      const durationMinutes = dwell.duration_seconds / 60;
      const baseRadius = 30; // Base radius in meters
      const maxRadius = 150; // Max radius in meters
      // Scale radius based on duration (logarithmic scaling)
      const radius = Math.min(maxRadius, baseRadius + Math.log10(durationMinutes + 1) * 30);

      return {
        type: 'Feature' as const,
        properties: {
          id: dwell.id,
          entity_id: dwell.entity_id,
          device_type: dwell.device_type,
          duration_seconds: dwell.duration_seconds,
          duration_minutes: durationMinutes,
          start_time: dwell.start_time,
          end_time: dwell.end_time,
          radius: radius,
          place_name: dwell.place_name || '',
          place_address: dwell.place_address || '',
          longitude: dwell.longitude,
          latitude: dwell.latitude,
          // Symbol used as the dwell-labels text-field. Prefer the geocoded
          // place name; if it isn't available, fall back to the rounded
          // coordinates so the label is still locationally meaningful (the
          // entity_id is intentionally NOT used here -- it would just repeat
          // the same student/device name on every dwell).
          label_text: dwell.place_name || `${dwell.latitude.toFixed(4)}, ${dwell.longitude.toFixed(4)}`
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [dwell.longitude, dwell.latitude] // [lon, lat]
        }
      };
    });

    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [filteredDwellLocations, showDwells]);

  const signalMarkersGeoJSON = useMemo(() => {
    if (!showSignalMarkers || signalMarkers.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return {
      type: 'FeatureCollection' as const,
      features: signalMarkers.filter(m => m.latitude && m.longitude).map((m, i) => ({
        type: 'Feature' as const,
        id: i,
        geometry: { type: 'Point' as const, coordinates: [m.longitude, m.latitude] },
        properties: { type: m.type, label: m.label, count: m.count, manufacturer: m.manufacturer || '', device_type: m.device_type || '' },
      })),
    };
  }, [signalMarkers, showSignalMarkers]);

  // Biometric highlight overlay — points where a sample fell in the selected HR zone.
  const biometricHighlightsGeoJSON = useMemo(() => {
    if (!biometricHighlights || biometricHighlights.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    return {
      type: 'FeatureCollection' as const,
      features: biometricHighlights
        .filter(p => Number.isFinite(p.latitude) && Number.isFinite(p.longitude))
        .map((p, i) => ({
          type: 'Feature' as const,
          id: i,
          geometry: { type: 'Point' as const, coordinates: [p.longitude, p.latitude] },
          properties: {
            value: p.value,
            zone: p.zone || '',
            color: p.color,
            timestamp: p.timestamp || '',
            label: p.label || `${Math.round(p.value)} bpm`,
          },
        })),
    };
  }, [biometricHighlights]);

  // Generate GeoJSON for itinerary route entries (markers and connecting lines)
  const itineraryGeoJSON = useMemo(() => {
    if (!showItinerary || itinerarySubmissions.length === 0) {
      return { type: 'FeatureCollection' as const, features: [] };
    }

    const features: any[] = [];

    itinerarySubmissions.forEach((submission) => {
      // Sort route entries by entry_order or arrive_datetime
      const sortedEntries = [...submission.route_entries].sort((a, b) => {
        const aTime = new Date(a.arrive_datetime).getTime();
        const bTime = new Date(b.arrive_datetime).getTime();
        return aTime - bTime;
      });

      // Add markers for each route entry
      sortedEntries.forEach((entry, index) => {
        const componentLabel = (entry.route_component || '').trim();
        features.push({
          type: 'Feature' as const,
          properties: {
            id: entry.id ?? index, // Use index as fallback if id is missing
            submission_id: submission.id,
            student_number: submission.student_number,
            location_name: entry.location_name_address,
            route_type: entry.route_type,
            route_component: componentLabel,
            arrive_datetime: entry.arrive_datetime,
            depart_datetime: entry.depart_datetime,
            entry_order: index,
            stop_number: index + 1,
            marker_type: 'itinerary'
          },
          geometry: {
            type: 'Point' as const,
            coordinates: [entry.longitude, entry.latitude] // [lon, lat]
          }
        });
      });

      // Add line connecting route entries in order
      if (sortedEntries.length > 1) {
        const coordinates = sortedEntries.map(entry => [entry.longitude, entry.latitude]);
        features.push({
          type: 'Feature' as const,
          properties: {
            submission_id: submission.id,
            student_number: submission.student_number,
            route_type: 'itinerary-route'
          },
          geometry: {
            type: 'LineString' as const,
            coordinates
          }
        });
      }
    });

    return {
      type: 'FeatureCollection' as const,
      features
    };
  }, [itinerarySubmissions, showItinerary]);


  // Handle click on points
  const handleClick = useCallback((event: any) => {
    // If in measurement mode, handle point addition
    if (isMeasuring) {
      const { lng, lat } = event.lngLat;
      setMeasurementPoints((prev) => nextMeasurePoints(measureMode, prev, [lng, lat]));
      return;
    }
    
    // If in drawing mode, handle circle creation
    if (isDrawingMode && onCircleDrawn) {
      const { lng, lat } = event.lngLat;
      onCircleDrawn([lat, lng], defaultRadius);
      return;
    }

    // If in annotation mode, add vertex to polygon being drawn
    if (isAnnotationMode) {
      const { lng, lat } = event.lngLat;
      setAnnotationVertices(prev => [...prev, [lng, lat]]);
      return;
    }

    const features = event.features;
    if (!features || features.length === 0) return;
    // Reference layers (boundaries etc.) sit under everything else: prefer any
    // non-reference hit, and only open a reference popup when that's all there is.
    const nonRef = features.find((f: any) => referenceLayerIdFromMapLayer(f.layer?.id) == null);
    const feature = nonRef ?? features[0];
    if (!nonRef) {
      const layerId = referenceLayerIdFromMapLayer(feature.layer?.id);
      if (layerId != null) {
        const geom = feature.geometry as GeoJSON.Geometry | undefined;
        const pt = geom?.type === 'Point' ? geom.coordinates : null;
        setPopupInfo({
          longitude: pt ? Number(pt[0]) : event.lngLat.lng,
          latitude: pt ? Number(pt[1]) : event.lngLat.lat,
          layerId: feature.layer.id,
          type: 'reference',
          referenceLayerId: layerId,
          properties: feature.properties || {},
        });
      }
      return;
    }

    // Expand clusters on click (GPS points, signal markers, cameras, PTT)
    if (
      feature.layer &&
      (feature.layer.id === 'clusters' ||
        feature.layer.id === 'signal-clusters' ||
        feature.layer.id === 'ptt-clusters' ||
        feature.layer.id === CAMERA_OVERLAY_LAYER_IDS.clusters)
    ) {
      const clusterId = feature.properties.cluster_id;
      const sourceId =
        feature.layer.id === 'signal-clusters'
          ? 'signal-markers-clustered'
          : feature.layer.id === 'ptt-clusters'
            ? 'ptt-messages-clustered'
          : feature.layer.id === CAMERA_OVERLAY_LAYER_IDS.clusters
            ? CAMERA_OVERLAY_LAYER_IDS.source
            : 'points-source';
      const mapboxMap = mapRef.current?.getMap?.();
      try {
        const source: any = mapboxMap?.getSource(sourceId);
        if (source && source.getClusterExpansionZoom) {
          source.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
            if (err) return;
            mapboxMap?.easeTo({ center: event.lngLat, zoom });
          });
        }
      } catch (e) {
        console.warn('Cluster expand failed:', e);
      }
      return;
    }

    if (
      feature.layer &&
      (feature.layer.id === CAMERA_OVERLAY_LAYER_IDS.unclustered ||
        feature.layer.id === CAMERA_OVERLAY_LAYER_IDS.labels)
    ) {
      setPopupInfo({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        layerId: feature.layer.id,
        type: 'camera',
        properties: feature.properties,
      });
      return;
    }

    if (feature.layer && (feature.layer.id === 'points-layer' || feature.layer.id === 'unclustered-points' || feature.layer.id === 'clusters-text' || feature.layer.id === 'ptt-unclustered' || feature.layer.id === 'ptt-points' || feature.layer.id === 'co-traveler-points' || feature.layer.id === 'co-traveler-connections' || feature.layer.id === 'co-traveler-heatmap-layer' || feature.layer.id === 'circle-centers' || feature.layer.id === 'digital-pol-icons' || feature.layer.id === 'itinerary-markers' || feature.layer.id === 'itinerary-numbers' || feature.layer.id === 'dwell-circles' || feature.layer.id === 'dwell-labels' || feature.layer.id === 'pf-adtech-dot' || feature.layer.id === 'pf-adtech-halo')) {
      setPopupInfo({
        longitude: event.lngLat.lng,
        latitude: event.lngLat.lat,
        layerId: feature.layer.id,
        properties: feature.properties
      });
    }
  }, [isDrawingMode, onCircleDrawn, defaultRadius, isAnnotationMode, onAnnotationDrawn, isMeasuring, measureMode]);

  // Track previous center for detecting jumps
  const prevCenterRef = React.useRef<[number, number] | null>(null);
  
  // Track whether map movement is user-initiated (panning/zooming) vs programmatic
  const isUserInteractionRef = React.useRef(false);

  // Track whether the current camera move was triggered programmatically (e.g., flyTo).
  // This prevents feedback loops where flyTo -> onMoveEnd -> onMapChange -> new mapSettings -> flyTo...
  const isProgrammaticMoveRef = React.useRef(false);
  const prevAppliedZoomRef = React.useRef<number | null>(null);
  
  // Update viewport when map settings change (ONLY for programmatic updates)
  useEffect(() => {
    const newLat = mapSettings.center[0];
    const newLng = mapSettings.center[1];
    const newZoom = mapSettings.zoom;
    const prevCenter = prevCenterRef.current;
    
    // If this update came from user interaction (panning/zooming), just update the ref and skip flyTo
    if (isUserInteractionRef.current) {
      prevCenterRef.current = [newLat, newLng];
      isUserInteractionRef.current = false;
      return;
    }
    
    // Calculate distance from previous center (if exists)
    let isLargeJump = false;
    let hasSignificantZoomChange = false;
    
    if (prevCenter) {
      const latDiff = Math.abs(newLat - prevCenter[0]);
      const lngDiff = Math.abs(newLng - prevCenter[1]);
      // Lowered threshold: If moved more than ~1km (0.01 degrees), consider it a "jump to" action
      isLargeJump = latDiff > 0.01 || lngDiff > 0.01;
      
      // Check for significant zoom change (> 2 zoom levels)
      const prevAppliedZoom = prevAppliedZoomRef.current;
      const zoomDiff = typeof prevAppliedZoom === 'number' ? Math.abs(newZoom - prevAppliedZoom) : 0;
      hasSignificantZoomChange = zoomDiff > 2;
    }
    
    // Update if followMode is on OR if this is a programmatic jump (location or zoom change)
    if (followMode || isLargeJump || hasSignificantZoomChange) {
      setViewport({
        longitude: newLng,
        latitude: newLat,
        zoom: newZoom
      });

      prevAppliedZoomRef.current = newZoom;
      
      // Also use flyTo for smooth animation on jumps
      if ((isLargeJump || hasSignificantZoomChange) && mapRef.current) {
        isProgrammaticMoveRef.current = true;
        mapRef.current.flyTo({
          center: [newLng, newLat],
          zoom: newZoom,
          duration: 1000
        });
      }
    }
    
    // Update previous center reference
    prevCenterRef.current = [newLat, newLng];
    if (prevAppliedZoomRef.current === null) {
      prevAppliedZoomRef.current = newZoom;
    }
  }, [mapSettings.center[0], mapSettings.center[1], mapSettings.zoom, followMode]);

  // Fit to full extent when data loads.
  //
  // ONE-SHOT semantics: fit once when data first becomes non-empty, then leave
  // the camera alone. Previously the effect also re-fit whenever the data
  // "center" moved more than ~50km, but the `dataPoints` prop is the
  // already-filtered visible set — so co-traveler points loading in,
  // visibility toggles flipping, time-window narrowing, uploaded files
  // appearing, etc. all shifted the centroid enough to retrigger the fit.
  // That's what caused the user-reported "first view is correct, then it
  // zooms in" behaviour. The right answer: auto-fit is for the "I just loaded
  // data, take me there" experience only; once the user is looking at the
  // map, every subsequent camera move should be theirs (or a deliberate
  // jump like an annotation click), never an opportunistic re-fit.
  const hasDataPointsRef = React.useRef(false);
  const lastDataCenterRef = React.useRef<{ lat: number; lng: number } | null>(null);
  
  useEffect(() => {
    if (!mapRef.current || dataPoints.length === 0) {
      return;
    }
    
    const map = mapRef.current.getMap?.();
    if (!map) {
      return;
    }
    
    // Extract coordinates with validation
    const validCoordinates = dataPoints
      .filter(p => p.coordinates && Array.isArray(p.coordinates) && p.coordinates.length >= 2)
      .map(p => ({
        lat: typeof p.coordinates[0] === 'number' ? p.coordinates[0] : null,
        lng: typeof p.coordinates[1] === 'number' ? p.coordinates[1] : null
      }))
      .filter(coord => coord.lat !== null && coord.lng !== null && !isNaN(coord.lat) && !isNaN(coord.lng));
    
    if (validCoordinates.length === 0) {
      return;
    }
    
    // Calculate bounds without spread operator to avoid stack overflow on large datasets
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    validCoordinates.forEach(c => {
      if (c.lat! < minLat) minLat = c.lat!;
      if (c.lat! > maxLat) maxLat = c.lat!;
      if (c.lng! < minLng) minLng = c.lng!;
      if (c.lng! > maxLng) maxLng = c.lng!;
    });
    
    // Calculate center of new data
    const newCenterLat = (minLat + maxLat) / 2;
    const newCenterLng = (minLng + maxLng) / 2;
    
    // Remember the most recent data center for diagnostics / future use, but
    // do NOT use it to gate the fit — see comment above the effect.
    lastDataCenterRef.current = { lat: newCenterLat, lng: newCenterLng };
    
    // Auto-fit only on the very first non-empty data load. Any subsequent
    // change in `dataPoints` (timeline tick, co-traveler toggle, uploaded
    // file, etc.) leaves the camera exactly where it is.
    if (!hasDataPointsRef.current) {
      hasDataPointsRef.current = true;
      
      // Calculate appropriate zoom level based on bounds
      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);
      // Zoom ladder. Lower zoom = more zoomed out. Each rung is roughly
      // tuned to leave a comfortable margin around the data bounds.
      // The final `else → 2` rung is the confirmed-good continental view.
      let newZoom = 10;
      if (maxDiff < 0.01) newZoom = 13;       // ~1 km — block / building
      else if (maxDiff < 0.05) newZoom = 12;  // ~5 km — neighborhood
      else if (maxDiff < 0.1)  newZoom = 11;  // ~10 km — small city
      else if (maxDiff < 0.2)  newZoom = 10;  // ~20 km — metro (e.g. Tucson)
      else if (maxDiff < 0.5)  newZoom = 9;   // ~50 km — large metro
      else if (maxDiff < 1)    newZoom = 8;   // ~100 km — region
      else if (maxDiff < 5)    newZoom = 6;   // ~500 km — state
      else if (maxDiff < 20)   newZoom = 4;   // ~2,000 km — multi-state
      else newZoom = 2;                       // continental / global

      // Update local viewport state first
      setViewport({
        latitude: newCenterLat,
        longitude: newCenterLng,
        zoom: newZoom
      });
      
      // Update prev center ref BEFORE calling onMapChange to prevent flyTo from triggering
      prevCenterRef.current = [newCenterLat, newCenterLng];
      prevAppliedZoomRef.current = newZoom;
      
      // Mark as programmatic move so flyTo doesn't fight fitBounds
      isProgrammaticMoveRef.current = true;
      
      // Notify parent of map change (updates Zustand store)
      if (onMapChange) {
        onMapChange([newCenterLat, newCenterLng], newZoom);
      }
    
    try {
      map.fitBounds([[minLng, minLat], [maxLng, maxLat]], {
        padding: 50,
        duration: 1000,
        // A tight cluster of points (e.g. a phone sitting still) would otherwise
        // fit to z19+, where the dark basemap has nothing to draw.
        maxZoom: 17,
      });
    } catch (error) {
      console.error('❌ Failed to set map bounds:', error);
    }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPoints]); // Only re-run when dataPoints changes, onMapChange is stable

  // Memoize base map style to avoid reloading style every render
  // Mapbox style URL selection - now using local mapStyle state
  const resolvedMapStyle = useMemo(() => {
    switch (mapStyle) {
      case 'satellite':
        return 'mapbox://styles/mapbox/satellite-v9';
      case 'satellite-streets':
        return 'mapbox://styles/mapbox/satellite-streets-v12';
      case 'dark':
        return 'mapbox://styles/mapbox/dark-v11';
      case 'light':
        return 'mapbox://styles/mapbox/light-v11';
      case 'outdoors':
        return 'mapbox://styles/mapbox/outdoors-v12';
      case 'streets':
      default:
        return 'mapbox://styles/mapbox/streets-v12';
    }
  }, [mapStyle]);

  // Add 3D buildings layer
  // Latest 3D flag for the deferred ('idle') path, so a toggle-off while the
  // style is still loading does not get a buildings layer added afterwards.
  const is3DModeRef = useRef(is3DMode);
  useEffect(() => {
    is3DModeRef.current = is3DMode;
  }, [is3DMode]);
  const add3DBuildingsLayer = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map || !is3DModeRef.current) return;

    try {
      // Check if the style is loaded - if not, wait for it. isStyleLoaded() is
      // also false while *sources/tiles* are still loading, and in that case no
      // further 'styledata' event may ever arrive (which is why 3D used to need
      // a second toggle). 'idle' always fires once the map settles.
      if (!map.isStyleLoaded()) {
        map.once('idle', add3DBuildingsLayer);
        return;
      }
      
      // Check if the layer already exists (authoritative check)
      if (map.getLayer('add-3d-buildings')) return;

      const layers = map.getStyle().layers;
      const labelLayerId = layers?.find(
        (layer: any) => layer.type === 'symbol' && layer.layout?.['text-field']
      )?.id;

      map.addLayer(
        {
          id: 'add-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 15,
          paint: {
            'fill-extrusion-color': '#aaa',
            'fill-extrusion-height': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'height']
            ],
            'fill-extrusion-base': [
              'interpolate',
              ['linear'],
              ['zoom'],
              15,
              0,
              15.05,
              ['get', 'min_height']
            ],
            'fill-extrusion-opacity': 0.6
          }
        },
        labelLayerId
      );
    } catch (error) {
      console.error('Error adding 3D buildings layer:', error);
    }
  }, []); // We check map.getLayer() directly instead of tracking state

  // Zoom controls
  const handleZoomIn = useCallback(() => {
    mapRef.current?.getMap().zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    mapRef.current?.getMap().zoomOut();
  }, []);

  const handleResetBearing = useCallback(() => {
    mapRef.current?.getMap().resetNorth();
  }, []);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (!mapContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      // Fullscreen the whole map column when the page provides one so the
      // floating timeline (a sibling of this map) comes along; otherwise
      // only the map canvas would be visible and the slider would vanish.
      const target =
        (mapContainerRef.current.closest('[data-pathfinder-map-column]') as HTMLElement | null) ||
        mapContainerRef.current;
      target.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, []);

  // Tooltip text for tools that only work in fullscreen.
  const fullscreenOnlyTitle = (title: string) => (isFullscreen ? title : `${title} - fullscreen only`);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      // Story panel, dwell insets and the route-sequence table are
      // fullscreen-only presentation tools: put them away when leaving.
      if (!fs) {
        setRouteSequenceOpen(false);
        setDwellInsetMenuAnchor(null);
        setCameraToolMenuAnchor(null);
        setHudPanelOpen('story', false);
        setHudPanelOpen('ai', false);
      }
      setDwellInsetsVisible(fs);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Calculate distance for measurement tool (returns km)
  const calculateMeasurementDistance = useCallback((lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in kilometers
  }, []);

  // Update total distance when measurement points change
  useEffect(() => {
    if (measurementPoints.length < 2) {
      setTotalDistance(0);
      return;
    }
    
    let total = 0;
    for (let i = 1; i < measurementPoints.length; i++) {
      total += calculateMeasurementDistance(
        measurementPoints[i - 1][1], measurementPoints[i - 1][0],
        measurementPoints[i][1], measurementPoints[i][0]
      );
    }
    setTotalDistance(total);
  }, [measurementPoints, calculateMeasurementDistance]);

  // Clear measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  const changeMeasureMode = useCallback((mode: MeasureMode) => {
    setMeasureMode(mode);
    setMeasurementPoints([]);
    setTotalDistance(0);
  }, []);

  // Toggle measurement mode
  const toggleMeasurement = useCallback(() => {
    if (isMeasuring) {
      setIsMeasuring(false);
    } else {
      setIsMeasuring(true);
      clearMeasurement();
    }
  }, [isMeasuring, clearMeasurement]);

  // Format distance for display
  const formatDistance = useCallback((km: number): string => {
    if (km < 1) {
      return `${(km * 1000).toFixed(0)} m`;
    }
    return `${km.toFixed(2)} km (${(km * 0.621371).toFixed(2)} mi)`;
  }, []);

  // Push the current viewport bounds to the parent. Strictly read-only — does
  // NOT touch any camera control state. Used by the "Crop tables to map view"
  // toggle so right-panel tabs can filter to what's actually on screen.
  const emitMapBounds = useCallback(() => {
    const map = mapRef.current?.getMap?.();
    if (!map) return;
    try {
      const b = map.getBounds();
      if (!b) return;
      const bounds = {
        west: b.getWest(),
        south: b.getSouth(),
        east: b.getEast(),
        north: b.getNorth(),
      };
      // Legend scopes itself to what is on screen.
      setViewBounds(bounds);
      // Story HUD / badges / AI summary crop to the map view.
      useNarrativeStore.getState().setStoryViewBounds(bounds);
      onMapBoundsChange?.(bounds);
    } catch {
      // Bounds may briefly be undefined during style swaps — ignore.
    }
  }, [onMapBoundsChange]);

  // Legend scope: only list entities / dwells / files that are inside the
  // current viewport, so a zoomed-in legend is not cluttered with everything
  // that was loaded.
  const legendScope = useMemo(() => {
    if (!viewBounds) return null;
    const { west, south, east, north } = viewBounds;
    const inBounds = (lat: number, lng: number) =>
      Number.isFinite(lat) && Number.isFinite(lng) && lat >= south && lat <= north && lng >= west && lng <= east;
    const norm = (s: string) => s.toLowerCase().replace(/\s*-\s*/g, '-').replace(/\s+/g, '');
    const ids = new Set<string>();
    const add = (s?: string) => {
      if (s) ids.add(norm(s));
    };
    const fileCounts: Record<string, number> = {};
    for (const p of dataPoints) {
      const c = p.coordinates;
      if (!c || !inBounds(c[0], c[1])) continue;
      add(p.deviceId);
      add(p.studentId);
      add((p as any).coTravelerKey);
      const file = (p.metadata?.originalFileName || '') as string;
      if (file) {
        const name = file.replace(/\.(csv|geojson|json|kml)$/i, '');
        fileCounts[name] = (fileCounts[name] || 0) + 1;
      }
    }
    for (const r of routes) {
      const coords = r.coordinates || [];
      // Sample long routes rather than testing every vertex.
      const step = Math.max(1, Math.floor(coords.length / 200));
      for (let i = 0; i < coords.length; i += step) {
        const c = coords[i];
        if (c && inBounds(c[0], c[1])) {
          add(r.deviceId);
          add(r.studentId);
          add((r as any).coTravelerKey);
          break;
        }
      }
    }
    let dwellsInView = 0;
    for (const d of dwellLocations) {
      if (inBounds(Number(d.latitude), Number(d.longitude))) dwellsInView++;
    }
    const has = (key: string) => ids.has(norm(key));
    return { has, dwellsInView, fileCounts };
  }, [viewBounds, dataPoints, routes, dwellLocations]);
  // Legend camera categories: only cameras actually drawn (near-track filter) and
  // inside the current view, not the whole catalog.
  const legendCameraCategories = useMemo<CameraCategoryLegendItem[]>(() => {
    if (!showCameraLocations) return [];
    const b = viewBounds;
    const counts: Record<string, number> = {};
    for (const f of cameraLayerData.features) {
      const c = f.geometry?.coordinates;
      if (!Array.isArray(c)) continue;
      const lon = Number(c[0]);
      const lat = Number(c[1]);
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
      if (b) {
        if (lat < b.south || lat > b.north) continue;
        const inLon = b.west <= b.east ? lon >= b.west && lon <= b.east : lon >= b.west || lon <= b.east;
        if (!inLon) continue;
      }
      const raw = f.properties?.category;
      const key = typeof raw === 'string' && raw.trim() ? raw.trim().toLowerCase() : CAMERA_FACET_UNKNOWN;
      counts[key] = (counts[key] || 0) + 1;
    }
    return cameraFacets.categories
      .filter((cat) => counts[cat.key] > 0)
      .map((cat) => ({ key: cat.key, label: cat.label, color: cat.color, visible: cat.visible, count: counts[cat.key] }));
  }, [showCameraLocations, cameraLayerData, viewBounds, cameraFacets.categories]);
  const legendCameraCount = useMemo(() => legendCameraCategories.reduce((n, c) => n + c.count, 0), [legendCameraCategories]);

  const legendStudentColors = useMemo(() => {
    if (!legendScope) return studentColors;
    const out: Record<string, string> = {};
    for (const k of Object.keys(studentColors)) if (legendScope.has(k)) out[k] = studentColors[k];
    return out;
  }, [legendScope, studentColors]);

  const legendCoTravelerItems = useMemo(
    () => (legendScope ? coTravelerLegendItems.filter((i) => legendScope.has(i.name)) : coTravelerLegendItems),
    [legendScope, coTravelerLegendItems],
  );

  const legendUploadedFiles = useMemo(() => {
    if (!legendScope) return uploadedFiles;
    return uploadedFiles
      .map((f) => ({ ...f, pointCount: legendScope.fileCounts[f.name] || 0 }))
      // Keep hidden files listed so they can be re-enabled from the legend.
      .filter((f) => !f.visible || f.pointCount > 0);
  }, [legendScope, uploadedFiles]);

  // Handle map load. Note: geocoder setup lives in its own effect below so
  // that re-binding this callback (deps churn) can't cause a duplicate
  // "Search for address" control. See the useEffect after this hook.
  const handleMapLoad = useCallback(() => {
    if (is3DMode) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    }

    // Expose map instance for client-side exports (PowerPoint numbering overlays, etc.)
    // This avoids threading map refs throughout the component tree.
    const map = mapRef.current?.getMap();
    (window as any).__pathfinderMap = map;
    if (map && !map.hasImage('ptt-message')) {
      // Drawn as a white alpha mask and registered as SDF so `icon-color`
      // tints it per sender and `icon-halo-*` draws the outline.
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, size, size);
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(17, 6);
        ctx.lineTo(47, 6);
        ctx.quadraticCurveTo(56, 6, 56, 15);
        ctx.lineTo(56, 33);
        ctx.quadraticCurveTo(56, 42, 47, 42);
        ctx.lineTo(17, 42);
        ctx.quadraticCurveTo(8, 42, 8, 33);
        ctx.lineTo(8, 15);
        ctx.quadraticCurveTo(8, 6, 17, 6);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(22, 42);
        ctx.lineTo(16, 54);
        ctx.lineTo(30, 42);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        // Punch the three "text" lines out of the bubble so they read in any tint.
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = 4;
        ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(18, 17); ctx.lineTo(46, 17); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(18, 25); ctx.lineTo(40, 25); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(18, 33); ctx.lineTo(34, 33); ctx.stroke();
        ctx.globalCompositeOperation = 'source-over';
        map.addImage('ptt-message', maskToSdf(ctx.getImageData(0, 0, size, size)), { pixelRatio: 2, sdf: true });
      }
    }

    // Initial bounds publish — without this the parent has no viewport to
    // filter by until the user touches the map.
    emitMapBounds();
  }, [is3DMode, add3DBuildingsLayer, emitMapBounds]);

  // Address search lives in the toolbar (not as a map control) so the
  // input does not sit on top of the view. Created when the popover opens.
  useEffect(() => {
    if (!searchAnchor || !geocoderBox) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const center = map.getCenter?.();
    const geocoder = new MapboxGeocoder({
      accessToken: process.env.REACT_APP_MAPBOX_TOKEN || '',
      mapboxgl: require('mapbox-gl') as any,
      marker: false,
      placeholder: 'Search address or place…',
      proximity: center
        ? ({ longitude: center.lng, latitude: center.lat } as any)
        : ({ longitude: -110.9265, latitude: 32.2217 } as any),
    });
    geocoderBox.appendChild(geocoder.onAdd(map));
    geocoderRef.current = geocoder;
    const onResult = (e: any) => {
      const c = e?.result?.center;
      if (Array.isArray(c) && c.length >= 2) {
        map.flyTo({ center: c as [number, number], zoom: Math.max(map.getZoom?.() ?? 12, 14), duration: 800 });
      }
      setSearchAnchor(null);
    };
    geocoder.on('result', onResult);
    requestAnimationFrame(() => geocoderBox.querySelector('input')?.focus());

    return () => {
      geocoder.off('result', onResult);
      try {
        geocoder.onRemove(map);
      } catch {
        /* already torn down */
      }
      geocoderRef.current = null;
      geocoderBox.replaceChildren();
    };
  }, [searchAnchor, geocoderBox]);

  // Handle window/container resize - force map to recalculate size.
  //
  // We debounce the resize call. Calling map.resize() on every animation
  // frame during a CSS transition (e.g. side-panel collapse) makes Mapbox
  // reallocate its WebGL backing store ~60 times in 300ms, which produces
  // a visible white flash that reads as "the map is reloading". POL avoids
  // this by calling map.resize() exactly once after the transition ends;
  // we replicate that here by waiting for the container to stop changing
  // size before resizing the map.
  useEffect(() => {
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    const RESIZE_SETTLE_MS = 120; // > one animation frame, < a typical 300ms transition

    const triggerResize = (delay = RESIZE_SETTLE_MS) => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        const map = mapRef.current?.getMap?.();
        if (map) {
          try {
            map.resize();
          } catch {
            // Map may be torn down during navigation.
          }
        }
      }, delay);
    };

    const handleWindowResize = () => triggerResize();

    window.addEventListener('resize', handleWindowResize);

    let observer: ResizeObserver | null = null;
    const container = mapContainerRef.current;
    if (container && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        // The observer fires on every frame of a CSS transition; the debounce
        // collapses that storm into a single resize once the layout settles.
        triggerResize();
      });
      observer.observe(container);
    }

    // Also trigger resize on mount to handle initial render issues
    triggerResize(0);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      if (observer) observer.disconnect();
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, []);

  // Re-add buildings layer when 3D mode changes or map style changes
  useEffect(() => {
    if (is3DMode && mapRef.current) {
      setTimeout(() => add3DBuildingsLayer(), 100);
    } else if (!is3DMode && mapRef.current) {
      const map = mapRef.current.getMap();
      if (map && map.getLayer('add-3d-buildings')) {
        map.removeLayer('add-3d-buildings');
      }
    }
  }, [is3DMode, resolvedMapStyle, add3DBuildingsLayer]);

  return (
      <Box ref={mapContainerRef} id="pathfinder-map-container" sx={{ 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        backgroundColor: theme.palette.mode === 'dark' ? '#111418' : theme.palette.background.default,
        // If Mapbox needs a frame to resize during panel dragging, keep the
        // exposed backing color close to the map chrome instead of flashing
        // white/light gray.
        '& .mapboxgl-map, & .mapboxgl-canvas-container, & .mapboxgl-canvas': {
          backgroundColor: theme.palette.mode === 'dark' ? '#111418' : theme.palette.background.default,
        },
        // Hide the geocoder search bar on mobile
        '& .mapboxgl-ctrl-geocoder': {
          display: { xs: 'none', md: 'block' },
        },
      }}>
        <Map
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          ref={mapRef}
          longitude={viewport.longitude}
          latitude={viewport.latitude}
          zoom={viewport.zoom}
          reuseMaps={true}
          projection={{name: 'mercator'}}
          attributionControl={true}
          preserveDrawingBuffer={true}
          style={{ 
            width: '100%', 
            height: '100%',
            minHeight: '400px', // Ensure minimum height
            cursor: (isDrawingMode || isAnnotationMode || isMeasuring) ? 'crosshair' : 'auto'
          }}
          mapStyle={resolvedMapStyle}
          renderWorldCopies={false}
          terrain={is3DMode ? { source: 'mapbox-dem', exaggeration: 1.5 } : undefined}
          pitch={is3DMode ? 45 : 0}
          bearing={is3DMode ? 0 : 0}
        onMove={(evt: any) => {
          // Only update local viewport state during move (for smooth visual feedback)
          setViewport(evt.viewState);
        }}
        onMoveEnd={(evt: any) => {
          if (!evt?.viewState) return;

          // Always publish the new viewport bounds so the "Crop tables to map
          // view" filter stays in sync (whether the move was user-driven or
          // programmatic — the visible extent has changed either way).
          emitMapBounds();

          // If this move was triggered by our own flyTo(), don't bounce the camera by
          // calling onMapChange again (which updates mapSettings and can re-trigger flyTo).
          if (isProgrammaticMoveRef.current) {
            isProgrammaticMoveRef.current = false;
            prevCenterRef.current = [evt.viewState.latitude, evt.viewState.longitude];
            prevAppliedZoomRef.current = evt.viewState.zoom;
            return;
          }

          // Only notify parent when user finishes moving (reduces re-renders)
          if (onMapChange) {
            isUserInteractionRef.current = true;
            onMapChange([evt.viewState.latitude, evt.viewState.longitude], evt.viewState.zoom);
          }
        }}
        onClick={handleClick}
        onDblClick={(event: any) => {
          // Double-click finishes annotation polygon
          if (isAnnotationMode && annotationVertices.length >= 3 && onAnnotationDrawn) {
            event.preventDefault();
            const closedCoords = [...annotationVertices, annotationVertices[0]];
            const polygon = {
              type: 'Polygon',
              coordinates: [closedCoords]
            };
            onAnnotationDrawn(polygon);
            setAnnotationVertices([]);
          }
        }}
        onLoad={(_evt: any) => {
          handleMapLoad();
        }}
        onError={(err: any) => {
          console.error('[PathfinderMapGL] Map ERROR:', err);
        }}
        interactiveLayerIds={['points-layer', 'unclustered-points', 'clusters', 'clusters-text', 'ptt-clusters', 'ptt-clusters-text', 'ptt-unclustered', 'ptt-points', 'signal-clusters', 'signal-clusters-text', 'signal-unclustered', 'signal-wifi', 'signal-bluetooth', 'signal-cellular', 'co-traveler-points', 'co-traveler-connections', 'co-traveler-heatmap-layer', 'circle-centers', 'circle-outlines', 'dwell-circles', 'dwell-labels', 'digital-pol-icons', 'itinerary-markers', 'itinerary-numbers', 'pf-adtech-dot', 'pf-adtech-halo', CAMERA_OVERLAY_LAYER_IDS.clusters, CAMERA_OVERLAY_LAYER_IDS.unclustered, CAMERA_OVERLAY_LAYER_IDS.labels, ...referenceInteractiveIds]}
      >
        {/* Scale Control */}
        <ScaleControl position="bottom-right" />

        {/* 3D Terrain Source */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {/* Admin reference layers (boundaries / districts / POIs…). The invisible
            anchor is always mounted first; reference layers insert beneath it so
            tracks, dwells and cameras stay on top even when toggled on later. */}
        <Layer id={REF_LAYERS_ANCHOR_ID} type="background" paint={{ 'background-opacity': 0 }} />
        <ReferenceLayersMapLayers layers={referenceLayers.layers} beforeId={REF_LAYERS_ANCHOR_ID} />

        <FullscreenProductTour
          active={isFullscreen}
          replayKey={tourReplay}
          onEnsureToolbar={() => setMapToolbarCollapsed(false)}
        />
        <ProductExportDialog
          open={productExportOpen}
          onClose={() => setProductExportOpen(false)}
          captureFallback={() => mapContainerRef.current}
          routeSequenceGroups={routeSequenceGroups}
          routeSequenceOpen={routeSequenceOpen}
          legend={
            showCameraLocations
              ? { cameraCategories: legendCameraCategories.map((c) => ({ label: c.label, count: c.count })), cameraCount: legendCameraCount }
              : null
          }
        />

        {/* Unified Map Controls Panel */}
        <Paper
          data-export-hide
          elevation={2}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : 'none',
            transition: 'width 160ms ease, opacity 160ms ease',
          }}
        >
          <Tooltip
            title={mapToolbarCollapsed ? 'Expand toolbar' : 'Collapse toolbar'}
            placement="left"
          >
            <IconButton
              onClick={() => setMapToolbarCollapsed((v) => !v)}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              aria-label={mapToolbarCollapsed ? 'Expand map toolbar' : 'Collapse map toolbar'}
            >
              {mapToolbarCollapsed ? (
                <ExpandToolbarIcon fontSize="small" />
              ) : (
                <CollapseToolbarIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title="Search address or place" placement="left">
            <IconButton
              onClick={(e) => setSearchAnchor((cur) => (cur ? null : e.currentTarget))}
              size="small"
              aria-label="Search address or place"
              sx={{
                borderRadius: 0,
                p: 1,
                color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                bgcolor: searchAnchor ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(searchAnchor)}
            anchorEl={searchAnchor}
            onClose={() => setSearchAnchor(null)}
            anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
            transformOrigin={{ vertical: 'center', horizontal: 'right' }}
            data-export-hide
            slotProps={{
              paper: {
                sx: {
                  p: 0.5,
                  overflow: 'visible',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(30,30,30,0.98)' : 'rgba(255,255,255,0.98)',
                },
              },
            }}
          >
            <Box
              ref={setGeocoderBox}
              sx={{
                '& .mapboxgl-ctrl-geocoder': {
                  boxShadow: 'none',
                  minWidth: 280,
                  width: 300,
                  maxWidth: '70vw',
                },
                '& .mapboxgl-ctrl-geocoder--input': { fontSize: '0.85rem' },
              }}
            />
          </Popover>

          {!mapToolbarCollapsed && (
            <>
          {/* Zoom In */}
          <Tooltip title="Zoom In" placement="left">
            <IconButton onClick={handleZoomIn} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Zoom Out */}
          <Tooltip title="Zoom Out" placement="left">
            <IconButton onClick={handleZoomOut} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <ZoomOutIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Reset North */}
          <Tooltip title="Reset North" placement="left">
            <IconButton onClick={handleResetBearing} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              <CompassIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Fullscreen */}
          <Tooltip title={isFullscreen ? "Exit Fullscreen" : "Fullscreen (enables Story panel, dwell insets, route sequence)"} placement="left">
            <IconButton onClick={toggleFullscreen} size="small" sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}>
              {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          {/* Export product: full-view capture + all widget data to HTML / PDF / PNG */}
          <Tooltip title="Export product (map view + widgets to HTML / PDF)" placement="left">
            <IconButton
              onClick={() => setProductExportOpen(true)}
              size="small"
              aria-label="Export product"
              data-tour="export"
              sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
            >
              <ProductExportIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={fullscreenOnlyTitle('Replay product tour')} placement="left">
            <span>
              <IconButton
                onClick={() => {
                  setMapToolbarCollapsed(false);
                  setTourReplay((n) => n + 1);
                }}
                disabled={!isFullscreen}
                size="small"
                aria-label="Replay product tour"
                sx={{
                  borderRadius: 0,
                  p: 1,
                  color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                }}
              >
                <ProductTourIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Cameras: Street View strip + pinned callouts for cameras the track passed.
              Opening either also brings up the Story timeline so the numbered
              sequence and the Street View cards appear together. */}
          {showCameraLocations && streetViewEnabled && (
            <>
              <Tooltip
                title={`Cameras${cameraCallouts.length ? ` (${cameraCallouts.length} pinned)` : ''}`}
                placement="left"
              >
                <IconButton
                  onClick={(e) => setCameraToolMenuAnchor(e.currentTarget)}
                  size="small"
                  aria-label="Cameras"
                  data-tour="cameras"
                  aria-haspopup="menu"
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    bgcolor: camerasInViewOpen ? 'rgba(0,131,143,0.25)' : 'transparent',
                    color:
                      cameraCallouts.length > 0 && cameraCalloutsVisible
                        ? '#26c6da'
                        : theme.palette.mode === 'dark'
                          ? 'white'
                          : 'inherit',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <CameraToolIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={cameraToolMenuAnchor}
                open={Boolean(cameraToolMenuAnchor)}
                onClose={() => setCameraToolMenuAnchor(null)}
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 270 } } }}
              >
                <MenuItem
                  onClick={() => {
                    const next = !camerasInViewOpen;
                    setCamerasInViewOpen(next);
                    if (next) openStoryTimeline();
                    setCameraToolMenuAnchor(null);
                  }}
                >
                  <ListItemIcon><StreetViewIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={camerasInViewOpen ? 'Hide cameras in view' : 'Cameras in view'}
                    secondary={
                      camerasInViewOpen
                        ? undefined
                        : streetViewPreviews
                          ? 'Street View of the nearest cameras on screen'
                          : 'List the nearest cameras on screen'
                    }
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={!isFullscreen || passedCameraItems.length === 0 || cameraCallouts.length >= CAMERA_CALLOUT_ALL_LIMIT}
                  onClick={() => {
                    pinCalloutsForPassedCameras();
                    openStoryTimeline();
                    setCameraToolMenuAnchor(null);
                  }}
                >
                  <ListItemIcon><CameraToolIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={
                      passedCameraItems.length === 0
                        ? 'No cameras passed on the loaded track'
                        : passedCameraItems.length > CAMERA_CALLOUT_ALL_LIMIT
                          ? `Pin the first ${CAMERA_CALLOUT_ALL_LIMIT} cameras passed`
                          : `Pin all ${passedCameraItems.length} cameras${passedCameraItems.length === 1 ? '' : 's'} passed`.replace('camerass', 'cameras')
                    }
                    secondary={isFullscreen ? 'Street View callouts with the Story timeline - or pin one from the strip' : 'Fullscreen only'}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={cameraCallouts.length === 0}
                  onClick={() => {
                    setCameraCalloutsVisible((v) => !v);
                    setCameraToolMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    {cameraCalloutsVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={cameraCalloutsVisible ? 'Hide callouts' : 'Show callouts'}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={cameraCallouts.length === 0}
                  onClick={() => {
                    setCameraCallouts([]);
                    setCameraToolMenuAnchor(null);
                  }}
                >
                  <ListItemIcon><ClearAllIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={`Clear callouts${cameraCallouts.length ? ` (${cameraCallouts.length})` : ''}`}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Story HUD: Story Mode timeline + AI summary over the map (fullscreen-friendly). */}
          <Tooltip
            title={
              routeSequenceOpen && isFullscreen
                ? 'Close the route sequence to open the Story panel'
                : fullscreenOnlyTitle(
                    !storyPanelOpen
                      ? 'Story panel (timeline + AI summary)'
                      : storyPanelMinimized
                        ? 'Open the minimized Story timeline'
                        : 'Hide Story panel',
                  )
            }
            placement="left"
          >
            <span style={{ display: 'flex' }}>
              <IconButton
                onClick={() => {
                  if (!storyPanelOpen) {
                    setRouteSequenceOpen(false);
                    toggleHudPanel('story');
                  } else if (storyPanelMinimized) {
                    setHudPanelMinimized('story', false);
                  } else {
                    toggleHudPanel('story');
                  }
                }}
                size="small"
                disabled={!isFullscreen || routeSequenceOpen}
                aria-label="Story panel"
                data-tour="story"
                aria-pressed={storyPanelOpen}
                sx={{
                  borderRadius: 0,
                  p: 1,
                  color: storyPanelOpen ? '#4fc3f7' : theme.palette.mode === 'dark' ? 'white' : 'inherit',
                  opacity: storyPanelOpen && storyPanelMinimized ? 0.6 : 1,
                  '&.Mui-disabled': { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)' },
                }}
              >
                <NarrativeIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          {/* Dwell insets: zoomed-in mini maps for chosen dwells (product screenshots). */}
          {showDwells && filteredDwellLocations.length > 0 && (
            <>
              <Tooltip title={fullscreenOnlyTitle(`Dwell insets${dwellInsets.length ? ` (${dwellInsets.length})` : ''}`)} placement="left">
                <span style={{ display: 'flex' }}>
                  <IconButton
                    onClick={(e) => setDwellInsetMenuAnchor(e.currentTarget)}
                    size="small"
                    disabled={!isFullscreen}
                    aria-label="Dwell insets"
                    data-tour="insets"
                    aria-haspopup="menu"
                    sx={{
                      borderRadius: 0,
                      p: 1,
                      color: dwellInsets.length > 0 && dwellInsetsVisible ? '#ff5252' : theme.palette.mode === 'dark' ? 'white' : 'inherit',
                      '&.Mui-disabled': { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)' },
                    }}
                  >
                    <DwellInsetIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                anchorEl={dwellInsetMenuAnchor}
                open={Boolean(dwellInsetMenuAnchor)}
                onClose={() => setDwellInsetMenuAnchor(null)}
                anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                slotProps={{ paper: { sx: { minWidth: 250 } } }}
              >
                <MenuItem
                  disabled={dwellInsets.length >= DWELL_INSET_ALL_LIMIT}
                  onClick={() => {
                    addInsetsForShownDwells();
                    setDwellInsetMenuAnchor(null);
                  }}
                >
                  <ListItemIcon><DwellInsetIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={
                      filteredDwellLocations.length > DWELL_INSET_ALL_LIMIT
                        ? `Inset the ${DWELL_INSET_ALL_LIMIT} longest dwells`
                        : `Inset all ${filteredDwellLocations.length} dwell${filteredDwellLocations.length === 1 ? '' : 's'}`
                    }
                    secondary="Or click a dwell and choose Add inset"
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={dwellInsets.length === 0}
                  onClick={() => {
                    setDwellInsetsVisible((v) => !v);
                    setDwellInsetMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    {dwellInsetsVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={dwellInsetsVisible ? 'Hide insets' : 'Show insets'}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={dwellInsets.length === 0}
                  onClick={() => {
                    setAllInsetsImagery(!allInsetsImagery);
                    setDwellInsetMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>{allInsetsImagery ? <MapLegendIcon fontSize="small" /> : <SatelliteIcon fontSize="small" />}</ListItemIcon>
                  <ListItemText
                    primary={allInsetsImagery ? 'Insets: use map basemap' : 'Insets: use imagery'}
                    secondary="Each inset also has its own toggle"
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                    secondaryTypographyProps={{ fontSize: '0.7rem' }}
                  />
                </MenuItem>
                <MenuItem
                  disabled={dwellInsets.length === 0}
                  onClick={() => {
                    setDwellInsets([]);
                    setDwellInsetMenuAnchor(null);
                  }}
                >
                  <ListItemIcon><ClearAllIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={`Clear insets${dwellInsets.length ? ` (${dwellInsets.length})` : ''}`}
                    primaryTypographyProps={{ fontSize: '0.85rem' }}
                  />
                </MenuItem>
              </Menu>
            </>
          )}

          {/* Route sequence: ordered cameras + dwells along the loaded track, exportable. */}
          {routeSequenceAvailable && (
            <Tooltip
              title={
                storyHudOpen && isFullscreen
                  ? 'Route sequence is unavailable while the Story panel is open (the timeline carries the numbering)'
                  : fullscreenOnlyTitle(routeSequenceOpen ? 'Hide route sequence' : 'Route sequence (cameras + dwells in order)')
              }
              placement="left"
            >
              <span style={{ display: 'flex' }}>
                <IconButton
                  onClick={() => setRouteSequenceOpen((v) => !v)}
                  size="small"
                  disabled={!isFullscreen || storyHudOpen}
                  aria-label="Route sequence"
                  aria-pressed={routeSequenceOpen}
                  sx={{
                    borderRadius: 0,
                    p: 1,
                    color: routeSequenceOpen ? theme.palette.primary.main : theme.palette.mode === 'dark' ? 'white' : 'inherit',
                    '&.Mui-disabled': { color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)' },
                  }}
                >
                  <RouteSequenceIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Time slider restore - only while the slider is minimized to its chip. */}
          {timeSliderMinimized && onRestoreTimeSlider && (
            <Tooltip title="Open time slider" placement="left">
              <IconButton
                onClick={onRestoreTimeSlider}
                size="small"
                aria-label="Open time slider"
                sx={{ borderRadius: 0, p: 1, color: '#4fc3f7', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <TimeSliderIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

          {/* My Location */}
          <Tooltip title="My Location" placement="left">
            <IconButton
              onClick={() => {
                navigator.geolocation?.getCurrentPosition(
                  (pos) => {
                    mapRef.current?.flyTo({
                      center: [pos.coords.longitude, pos.coords.latitude],
                      zoom: 14,
                      duration: 1000
                    });
                  },
                  (err) => console.error('Geolocation error:', err)
                );
              }}
              size="small"
              sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
            >
              <MyLocationIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* 3D Mode */}
          <Tooltip title={is3DMode ? "Switch to 2D" : "Switch to 3D"} placement="left">
            <IconButton
              onClick={() => setIs3DMode(!is3DMode)}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: is3DMode ? 'primary.main' : 'transparent',
                color: is3DMode ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                '&:hover': {
                  bgcolor: is3DMode ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>3D</Typography>
            </IconButton>
          </Tooltip>

          {/* Legend Toggle */}
          <Tooltip title={showLegend ? "Hide Legend" : "Show Legend"} placement="left">
            <IconButton
              onClick={() => setShowLegend(!showLegend)}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: showLegend ? 'primary.main' : 'transparent',
                color: showLegend ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                '&:hover': {
                  bgcolor: showLegend ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <MapLegendIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Map Settings — opens the centered MapLayerControlsDialog (mounted
              by CompactTimeSlider, where playback state lives). Same dialog
              that the slider's gear button opens, so users can reach Layers
              + Playback from either the map or the slider. The open boolean
              is owned by PathfinderPage via `onOpenMapSettings`. */}
          <Tooltip title="Map Settings (layers & playback)" placement="left">
            <IconButton
              onClick={() => onOpenMapSettings?.()}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: 'transparent',
                color: theme.palette.mode === 'dark' ? 'white' : 'inherit',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <MapSettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Dwell labels, itinerary, cameras and camera filters live in Map Settings. */}

          {/* Cameras in view moved up: see the camera tool below Fullscreen. */}


          {/* Crop tables to map viewport — only rendered when the parent
              wires the toggle. Lives on the map toolbar (rather than only
              in the page header) so analysts can flip it from where their
              attention already is. The map camera is never changed; only
              the right-side tabs (Charts, Co-Travelers, Dwell, Digital PoL,
              Annotations) re-scope to the current viewport. */}
          {onToggleFilterByMapExtent && (
            <Tooltip
              title={
                filterByMapExtent
                  ? 'Showing only entries inside the current map view (Charts, Co-Travelers, Dwell, Digital PoL, Annotations). Click to show all.'
                  : 'Crop the right-panel tabs to whatever is currently in view on the map.'
              }
              placement="left"
            >
              <IconButton
                onClick={onToggleFilterByMapExtent}
                size="small"
                sx={{
                  borderRadius: 0,
                  p: 1,
                  bgcolor: filterByMapExtent ? 'primary.main' : 'transparent',
                  color: filterByMapExtent
                    ? 'white'
                    : theme.palette.mode === 'dark'
                    ? 'white'
                    : 'inherit',
                  '&:hover': {
                    bgcolor: filterByMapExtent ? 'primary.dark' : 'action.hover',
                  },
                }}
              >
                <FilterByMapIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* AdTech results table toggle — only when signals are loaded.
              Fully hides/shows the floating results table without discarding
              the loaded signals (distinct from the table's own collapse/clear). */}
          {hasAdtechResults && onToggleAdtechTable && (
            <Tooltip
              title={adtechTableVisible ? 'Hide AdTech results table' : 'Show AdTech results table'}
              placement="left"
            >
              <IconButton
                onClick={onToggleAdtechTable}
                size="small"
                sx={{
                  borderRadius: 0,
                  p: 1,
                  bgcolor: adtechTableVisible ? '#e91e63' : 'transparent',
                  color: adtechTableVisible ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                  '&:hover': { bgcolor: adtechTableVisible ? '#c2185b' : 'action.hover' },
                }}
              >
                <AdtechTableIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Divider sx={{ borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'inherit' }} />

          {/* Map Style Selector */}
          <Tooltip title="Map Style" placement="left">
            <IconButton
              onClick={(e) => setMapStyleAnchor(e.currentTarget)}
              size="small"
              sx={{ borderRadius: 0, p: 1, color: theme.palette.mode === 'dark' ? 'white' : 'inherit' }}
            >
              <SatelliteIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Measure Distance */}
          <Tooltip title={isMeasuring ? "Cancel Measurement" : "Measure Distance"} placement="left">
            <IconButton
              onClick={toggleMeasurement}
              size="small"
              sx={{
                borderRadius: 0,
                p: 1,
                bgcolor: isMeasuring ? 'primary.main' : 'transparent',
                color: isMeasuring ? 'white' : (theme.palette.mode === 'dark' ? 'white' : 'inherit'),
                '&:hover': {
                  bgcolor: isMeasuring ? 'primary.dark' : 'action.hover',
                },
              }}
            >
              <MeasureIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Clear Measurement - only show when there are points */}
          {measurementPoints.length > 0 && (
            <Tooltip title="Clear Measurement" placement="left">
              <IconButton
                onClick={clearMeasurement}
                size="small"
                sx={{ borderRadius: 0, p: 1, color: 'error.main' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* GPS Cleanup (admin only, intentionally low-visibility).
              Lives at the BOTTOM of the toolbar and is colored to blend
              into the toolbar background so it isn't noticed by casual
              admins. Only the user who put it there knows where to
              click. The icon goes full-white when active (panel open)
              so the user can still find their way back to close it.
              Multi-layer gating still applies: gis-admin group on the
              frontend, PIN inside the panel, and the backend
              re-verifies both on every cleanup request. */}
          {canUseGpsCleanup && (
            <Tooltip
              title="GPS Cleanup (gis-admin)"
              placement="left"
            >
              <IconButton
                onClick={() => {
                  setCleanupOpen((v) => !v);
                  if (cleanupOpen) setCleanupPolygon(null);
                }}
                size="small"
                aria-label="GPS Cleanup"
                sx={{
                  borderRadius: 0,
                  p: 1,
                  bgcolor: cleanupOpen ? 'error.main' : 'transparent',
                  // Icon glyph matches the toolbar's actual Paper color
                  // exactly (in both modes) so the icon is invisible at
                  // rest. The toolbar is a <Paper> so its surface color
                  // is theme.palette.background.paper - using anything
                  // else (pure black, etc.) leaves a visible glyph
                  // against the dark-gray paper. When the panel is open
                  // the icon snaps to white so the admin can find it
                  // again to close.
                  color: cleanupOpen ? 'white' : theme.palette.background.paper,
                  // No hover highlight - we don't want a stray mouse to
                  // reveal the button via either icon contrast or a
                  // background-color flash.
                  '&:hover': {
                    bgcolor: cleanupOpen ? 'error.dark' : 'transparent',
                    color: cleanupOpen ? 'white' : theme.palette.background.paper,
                  },
                }}
              >
                <CleanupIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
            </>
          )}
        </Paper>

        {/* NOTE: The consolidated Map Settings dialog (layers + playback) is
            now mounted by CompactTimeSlider, which holds the playback state.
            The toolbar button above only signals intent via
            `onOpenMapSettings` (provided by PathfinderPage). Keeps the dialog
            in one place and avoids duplicating it across two components. */}

        {/* Map Style Popover */}
        <Popover
          open={Boolean(mapStyleAnchor)}
          anchorEl={mapStyleAnchor}
          onClose={() => setMapStyleAnchor(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Paper sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: '0.9rem' }}>
              Map Style
            </Typography>
            <Grid container spacing={1}>
              {[
                { value: 'streets', label: 'Streets' },
                { value: 'satellite', label: 'Satellite' },
                { value: 'satellite-streets', label: 'Hybrid' },
                { value: 'dark', label: 'Dark' },
                { value: 'light', label: 'Light' },
                { value: 'outdoors', label: 'Outdoors' }
              ].map((style) => (
                <Grid item xs={6} key={style.value}>
                  <Card
                    onClick={() => {
                      setMapStyle(style.value as MapStyleType);
                      setMapStyleAnchor(null);
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: mapStyle === style.value ? '2px solid #2196f3' : '1px solid #ddd',
                      '&:hover': {
                        boxShadow: 2
                      }
                    }}
                  >
                    <Box sx={{ p: 1, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {style.label}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Popover>

        <MapMeasureLayers
          mode={measureMode}
          points={measurementPoints}
          ringSpacingM={ringSpacingM}
          ringCount={ringCount}
          ringOpacity={ringOpacity}
        />

        {measurementPoints.map((point, index) => (
          <Marker key={`measure-point-${index}`} longitude={point[0]} latitude={point[1]}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: index === 0 ? '#4caf50' : '#ff5722',
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              }}
            />
          </Marker>
        ))}

        {/* Routes Layer - keep mounted to avoid flicker */}
        {/* Enhanced Heatmap Layers - conditional rendering with improved visuals */}
        {showHeatmap && (
          <>
            {/* Route Points Heatmap */}
            <Source id="route-heat" type="geojson" data={routePointsGeoJSON}>
              <Layer
                id="route-heatmap"
                type="heatmap"
                source="route-heat"
                paint={{
                  'heatmap-weight': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.5,
                    12, 1.0,
                    18, 1.5
                  ],
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.4,
                    10, 1.0,
                    15, 1.5,
                    18, 2.0
                  ],
                  'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(0, 0, 255, 0)',           // Transparent blue
                    0.1, 'rgba(0, 100, 255, 0.2)',     // Light blue
                    0.2, 'rgba(0, 150, 255, 0.4)',      // Medium blue
                    0.3, 'rgba(0, 200, 255, 0.6)',      // Cyan
                    0.4, 'rgba(0, 255, 200, 0.7)',      // Light cyan
                    0.5, 'rgba(100, 255, 100, 0.8)',    // Light green
                    0.6, 'rgba(200, 255, 0, 0.8)',      // Yellow-green
                    0.7, 'rgba(255, 200, 0, 0.9)',      // Orange
                    0.8, 'rgba(255, 150, 0, 0.9)',      // Dark orange
                    0.9, 'rgba(255, 100, 0, 0.95)',     // Red-orange
                    1, 'rgba(255, 0, 0, 1)'              // Pure red
                  ],
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 15,
                    10, 25,
                    15, 35,
                    18, 45
                  ],
                  'heatmap-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.6,
                    10, 0.4,
                    15, 0.3,
                    18, 0.2
                  ]
                }}
              />
            </Source>

            {/* Data Points Heatmap (when data points are visible) */}
            {showDataPoints && (
              <Source id="points-heat" type="geojson" data={pointsGeoJSON}>
                <Layer
                  id="points-heatmap"
                  type="heatmap"
                  source="points-heat"
                  paint={{
                    'heatmap-weight': [
                      'interpolate', ['linear'], ['zoom'],
                      5, 0.3,
                      12, 0.8,
                      18, 1.2
                    ],
                    'heatmap-intensity': [
                      'interpolate', ['linear'], ['zoom'],
                      5, 0.3,
                      10, 0.8,
                      15, 1.2,
                      18, 1.5
                    ],
                    'heatmap-color': [
                      'interpolate', ['linear'], ['heatmap-density'],
                      0, 'rgba(255, 0, 255, 0)',         // Transparent magenta
                      0.1, 'rgba(255, 0, 255, 0.1)',   // Light magenta
                      0.2, 'rgba(200, 0, 255, 0.2)',   // Purple
                      0.3, 'rgba(150, 0, 255, 0.3)',   // Medium purple
                      0.4, 'rgba(100, 0, 255, 0.4)',   // Dark purple
                      0.5, 'rgba(0, 0, 255, 0.5)',     // Blue
                      0.6, 'rgba(0, 100, 255, 0.6)',   // Light blue
                      0.7, 'rgba(0, 200, 255, 0.7)',   // Cyan
                      0.8, 'rgba(0, 255, 200, 0.8)',   // Light cyan
                      0.9, 'rgba(100, 255, 100, 0.9)', // Green
                      1, 'rgba(255, 255, 0, 1)'        // Yellow
                    ],
                    'heatmap-radius': [
                      'interpolate', ['linear'], ['zoom'],
                      5, 10,
                      10, 20,
                      15, 30,
                      18, 40
                    ],
                    'heatmap-opacity': [
                      'interpolate', ['linear'], ['zoom'],
                      5, 0.4,
                      10, 0.3,
                      15, 0.2,
                      18, 0.15
                    ]
                  }}
                />
              </Source>
            )}
          </>
        )}

        {/* Routes Layer - Only show when enabled */}
        {showRouteLines && (
          <>
            {/* Route lines */}
            <Source id="routes-source" type="geojson" data={routesGeoJSON}>
              <Layer
                id="routes-layer"
                type="line"
                layout={{
                  'line-join': 'round',
                  'line-cap': 'round'
                }}
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['get', 'width'],
                  'line-opacity': ['get', 'opacity']
                }}
              />
            </Source>
            
            {/* Direction arrow at leading edge of route - MUST be separate from routes-source */}
            {showDirectionArrows && (
              <Source id="route-heads" type="geojson" data={routeHeadsGeoJSON}>
                <Layer
                  id="route-arrows"
                  type="symbol"
                  layout={{
                    'text-field': '▲',
                    'text-rotate': ['get', 'bearing'],
                    'text-rotation-alignment': 'map',
                    'text-size': 20,
                    'text-allow-overlap': true,
                    'text-ignore-placement': true
                  }}
                  paint={{
                    'text-color': ['coalesce', ['get', 'color'], '#ffffff'],
                    'text-opacity': 1,
                    'text-halo-color': 'rgba(0,0,0,0.9)',
                    'text-halo-width': 3
                  }}
                />
              </Source>
            )}
          </>
        )}

        {/* Points Source and Layers (clustered or plain) - Only show when enabled */}
        {(() => {
          return showDataPoints;
        })() && (
          <Source
            id="points-source"
            key={pointsSourceKey}
            type="geojson"
            data={pointsGeoJSON}
            cluster={enableClustering}
            clusterMaxZoom={18}
            clusterRadius={80}
          >
          {enableClustering ? (
            <>
              <Layer
                id="clusters"
                type="circle"
                source="points-source"
                filter={["has", "point_count"]}
                paint={{
                  'circle-color': [
                    'interpolate', ['linear'], ['get', 'point_count'],
                    1, '#2196f3',
                    50, '#43a047',
                    100, '#fbc02d',
                    300, '#fb8c00',
                    800, '#e53935'
                  ],
                  'circle-radius': [
                    'interpolate', ['linear'], ['get', 'point_count'],
                    1, 12,
                    50, 16,
                    100, 20,
                    300, 26,
                    800, 32
                  ],
                  'circle-opacity': 0.55,
                  'circle-stroke-color': 'rgba(255,255,255,0.7)',
                  'circle-stroke-width': 1.2,
                  'circle-blur': 0.2
                }}
              />
              <Layer
                id="clusters-text"
                type="symbol"
                source="points-source"
                filter={["has", "point_count"]}
                layout={{
                  'text-field': ['to-string', ['get', 'point_count_abbreviated']],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 12
                }}
                paint={{ 'text-color': '#ffffff' }}
              />
              <Layer
                id="unclustered-points"
                type="circle"
                source="points-source"
                filter={["!has", "point_count"]}
                paint={{
                  'circle-radius': [
                    'case',
                    ['==', ['get', 'deviceType'], 'phone'], 3,
                    ['==', ['get', 'deviceType'], 'watch'], 2.5,
                    ['==', ['get', 'deviceType'], 'vehicle'], 3.5,
                    3
                  ],
                  'circle-color': ['get', 'color'],
                  'circle-stroke-color': '#FFFFFF',
                  'circle-stroke-width': 1,
                  'circle-opacity': ['get', 'opacity']
                }}
              />
            </>
          ) : (
            <Layer
              id="points-layer"
              type="circle"
              source="points-source"
              paint={{
                'circle-radius': [
                  'case',
                  ['==', ['get', 'deviceType'], 'phone'], 3,
                  ['==', ['get', 'deviceType'], 'watch'], 2.5,
                  ['==', ['get', 'deviceType'], 'vehicle'], 3.5,
                  3
                ],
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#FFFFFF',
                'circle-stroke-width': 1,
                'circle-opacity': ['get', 'opacity'],
                'circle-stroke-opacity': 1
              }}
            />
          )}
        </Source>
        )}

        {/* Ring around the SRP closest to the selected student. */}
        {highlightClosestSrp && closestSrpHaloGeoJSON.features.length > 0 && (
          <Source id="closest-srp-halo" type="geojson" data={closestSrpHaloGeoJSON}>
            <Layer
              id="closest-srp-halo-glow"
              type="circle"
              paint={{
                'circle-radius': 22,
                'circle-color': '#ffe082',
                'circle-blur': 0.75,
                'circle-opacity': 0.55,
              }}
            />
            <Layer
              id="closest-srp-halo-ring"
              type="circle"
              paint={{
                'circle-radius': 16,
                'circle-color': 'rgba(0,0,0,0)',
                'circle-stroke-width': 3,
                'circle-stroke-color': '#ffc107',
                'circle-stroke-opacity': 1,
              }}
            />
          </Source>
        )}

        {/* Entity number at the latest position. Shape of the marker is unchanged. */}
        {(showDataPoints || showRouteLines) && entityNumberGeoJSON.features.length > 0 && (
          <Source id="entity-number-labels" type="geojson" data={entityNumberGeoJSON}>
            <Layer
              id="entity-number-labels"
              type="symbol"
              layout={{
                'text-field': ['get', 'label'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 13,
                'text-anchor': 'bottom',
                'text-offset': [0, -0.6],
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{
                'text-color': '#ffffff',
                'text-halo-color': 'rgba(0,0,0,0.9)',
                'text-halo-width': 1.5,
              }}
            />
          </Source>
        )}

        {/* PTT bubbles render after the GPS points so they sit above the track
            clusters rather than being hidden underneath them. */}
        {showPttMessages && pttGeoJSON.features.length > 0 && (
          <Source
            id={pttSourceId}
            key={pttSourceId}
            type="geojson"
            data={pttGeoJSON}
            cluster={clusterPttMessages}
            clusterMaxZoom={16}
            clusterRadius={60}
          >
            {/* NOTE: layers must be direct children of <Source> (no fragment) so
                react-map-gl can inject the `source` prop into each one. */}
            {clusterPttMessages && (
            <Layer
              id="ptt-clusters"
              type="circle"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': [
                  'interpolate', ['linear'], ['get', 'point_count'],
                  1, '#29b6f6',
                  20, '#0288d1',
                  80, '#01579b',
                ],
                'circle-radius': [
                  'interpolate', ['linear'], ['get', 'point_count'],
                  1, 12,
                  20, 16,
                  80, 22,
                ],
                'circle-opacity': 0.7,
                'circle-stroke-color': 'rgba(255,255,255,0.8)',
                'circle-stroke-width': 1.2,
              }}
            />
            )}
            {clusterPttMessages && (
            <Layer
              id="ptt-clusters-text"
              type="symbol"
              filter={['has', 'point_count']}
              layout={{
                'text-field': ['to-string', ['get', 'point_count_abbreviated']],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
              }}
              paint={{ 'text-color': '#ffffff' }}
            />
            )}
            <Layer
              id={clusterPttMessages ? 'ptt-unclustered' : 'ptt-points'}
              type="symbol"
              {...(clusterPttMessages ? { filter: ['!', ['has', 'point_count']] as any } : {})}
              layout={pttIconLayout}
              paint={pttIconPaint}
            />
          </Source>
        )}

        {/* Leader lines: active PTT bubble → sender's track point at that moment */}
        {showPttMessages && pttLeaderGeoJSON.features.length > 0 && (
          <Source id="ptt-leaders" type="geojson" data={pttLeaderGeoJSON}>
            <Layer
              id="ptt-leaders-line"
              type="line"
              layout={{ 'line-cap': 'round' }}
              paint={{
                'line-color': ['get', 'color'],
                'line-width': 2,
                'line-dasharray': [1.5, 1.5],
                'line-opacity': 0.9,
              }}
            />
          </Source>
        )}

        {/* Co-traveler Visualization Layers */}
        {_showCoTravelerPoints && (
          <>
            {/* Co-traveler Encounter Points */}
            <Source id="co-traveler-encounters" type="geojson" data={coTravelerConnectionsGeoJSON}>
              <Layer
                id="co-traveler-points"
                type="circle"
                source="co-traveler-encounters"
                paint={{
                  'circle-radius': [
                    'interpolate',
                    ['linear'],
                    ['get', 'confidence'],
                    0, 4,              // Low confidence - small
                    0.5, 6,            // Medium confidence - medium
                    0.8, 8,            // High confidence - large
                    1, 10              // Perfect confidence - largest
                  ],
                  'circle-color': [
                    'case',
                    ['==', ['get', 'coTravelerEntityType'], 'SRP'], '#f44336',      // Red for SRP
                    ['==', ['get', 'coTravelerEntityType'], 'RP'], '#ff9800',       // Orange for RP
                    ['==', ['get', 'coTravelerEntityType'], 'WIFI'], '#4caf50',     // Green for Wi-Fi digital co-travelers
                    ['==', ['get', 'coTravelerEntityType'], 'BLUETOOTH'], '#2196f3', // Blue for Bluetooth
                    ['==', ['get', 'coTravelerEntityType'], 'CELLULAR'], '#ffc107', // Amber for Cellular
                    ['==', ['get', 'coTravelerEntityType'], 'ADTECH'], '#e91e63',   // Pink for AdTech (Venntel/GeoNeutron)
                    '#2196f3'  // Blue for Students (default)
                  ],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 1,
                  'circle-opacity': 0.8
                }}
              />
              
              {/* Co-traveler Point Labels */}
              <Layer
                id="co-traveler-labels"
                type="symbol"
                source="co-traveler-encounters"
                layout={{
                  'text-field': [
                    'concat',
                    ['get', 'primaryDevice'],
                    ' ↔ ',
                    ['get', 'coTravelerDevice']
                  ],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 10,
                  'text-offset': [0, -2],
                  'text-anchor': 'bottom'
                }}
                paint={{
                  'text-color': '#ffffff',
                  'text-halo-color': '#000000',
                  'text-halo-width': 1
                }}
              />
            </Source>
          </>
        )}

        {/* Co-traveler Connection Lines */}
        {_showCoTravelerLines && (
          <>
            <Source id="co-traveler-lines" type="geojson" data={coTravelerLinesGeoJSON}>
              <Layer
                id="co-traveler-connections"
                type="line"
                source="co-traveler-lines"
                paint={{
                  'line-color': [
                    'case',
                    ['==', ['get', 'coTravelerEntityType'], 'SRP'], '#f44336',      // Red for SRP
                    ['==', ['get', 'coTravelerEntityType'], 'RP'], '#ff9800',       // Orange for RP
                    ['==', ['get', 'coTravelerEntityType'], 'WIFI'], '#4caf50',     // Green for Wi-Fi digital co-travelers
                    ['==', ['get', 'coTravelerEntityType'], 'BLUETOOTH'], '#2196f3', // Blue for Bluetooth
                    ['==', ['get', 'coTravelerEntityType'], 'CELLULAR'], '#ffc107', // Amber for Cellular
                    ['==', ['get', 'coTravelerEntityType'], 'ADTECH'], '#e91e63',   // Pink for AdTech (Venntel/GeoNeutron)
                    '#2196f3'  // Blue for Students (default)
                  ],
                  'line-width': [
                    'interpolate',
                    ['linear'],
                    ['get', 'confidence'],
                    0, 2,              // Low confidence - thin
                    0.5, 3,            // Medium confidence - medium
                    0.8, 4,            // High confidence - thick
                    1, 5               // Perfect confidence - thickest
                  ],
                  'line-opacity': 0.7
                }}
              />
            </Source>
          </>
        )}

        {/* Co-traveler Heatmap Layer */}
        {_showCoTravelerHeatmap && (
          <>
            <Source id="co-traveler-heatmap" type="geojson" data={coTravelerHeatmapGeoJSON}>
              <Layer
                id="co-traveler-heatmap-layer"
                type="heatmap"
                source="co-traveler-heatmap"
                paint={{
                  'heatmap-weight': [
                    'interpolate', ['linear'], ['get', 'weight'],
                    0, 0.1,
                    1, 0.5,
                    5, 1.0,
                    10, 1.5
                  ],
                  'heatmap-intensity': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 0.3,
                    10, 0.8,
                    15, 1.2,
                    18, 1.5
                  ],
                  'heatmap-color': [
                    'interpolate', ['linear'], ['heatmap-density'],
                    0, 'rgba(255, 0, 0, 0)',           // Transparent red
                    0.1, 'rgba(255, 0, 0, 0.1)',       // Light red
                    0.2, 'rgba(255, 100, 0, 0.2)',     // Orange-red
                    0.3, 'rgba(255, 200, 0, 0.3)',     // Yellow-orange
                    0.4, 'rgba(255, 255, 0, 0.4)',     // Yellow
                    0.5, 'rgba(200, 255, 0, 0.5)',     // Yellow-green
                    0.6, 'rgba(100, 255, 0, 0.6)',     // Green-yellow
                    0.7, 'rgba(0, 255, 0, 0.7)',       // Green
                    0.8, 'rgba(0, 255, 100, 0.8)',     // Green-cyan
                    0.9, 'rgba(0, 255, 200, 0.9)',     // Cyan-green
                    1, 'rgba(0, 255, 255, 1)'          // Pure cyan
                  ],
                  'heatmap-radius': [
                    'interpolate', ['linear'], ['zoom'],
                    5, 15,
                    10, 25,
                    15, 35,
                    18, 45
                  ],
                  'heatmap-opacity': 0.6
                }}
              />
            </Source>
          </>
        )}

        {/* Dwell Locations (transparent red circles) */}
        {showDwells && dwellLocations.length > 0 && (
          <Source id="dwell-locations" type="geojson" data={dwellsGeoJSON}>
            <Layer
              id="dwell-circles"
              type="circle"
              source="dwell-locations"
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8, 5,  // At zoom 8, 5px radius
                  12, 15, // At zoom 12, 15px radius
                  16, 30, // At zoom 16, 30px radius
                  20, 50  // At zoom 20, 50px radius
                ],
                'circle-color': '#ff0000', // Red color
                'circle-opacity': 0.3, // More visible
                'circle-stroke-color': '#ff0000',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 0.8 // More visible stroke
              }}
            />
            {/*
              Optional dwell label layer. Mapbox handles dedup automatically:
              `text-allow-overlap: false` plus generous `text-padding` means
              labels that would visually collide are hidden, and the
              `symbol-sort-key` (negative duration) ensures the longer dwell
              wins the collision -- so a cluster of repeat visits to the same
              place renders ONE label on the most-significant dwell instead
              of stacking 5 copies of "Home".
            */}
            {showDwellLabels && (
              <Layer
                id="dwell-labels"
                type="symbol"
                source="dwell-locations"
                layout={{
                  'text-field': ['get', 'label_text'],
                  'text-size': [
                    'interpolate', ['linear'], ['zoom'],
                    10, 10,
                    14, 12,
                    18, 13,
                  ],
                  'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
                  'text-offset': [0, -1.4],
                  'text-anchor': 'bottom',
                  'text-allow-overlap': false,
                  'text-ignore-placement': false,
                  'text-padding': 12,
                  'text-max-width': 10,
                  'symbol-sort-key': ['*', -1, ['get', 'duration_seconds']],
                }}
                paint={{
                  'text-color': '#b00000',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1.5,
                  'text-halo-blur': 0.5,
                }}
              />
            )}
          </Source>
        )}

        {/* Story HUD event numbers (#1, #2 â€¦) - driven by the narrative store */}
        <StoryEventMapLabels dataPoints={dataPoints} />

        {/* Route sequence numbers (cameras passed + dwells, in order) */}
        {/* Story HUD badges carry the same numbers (cameras included), so avoid a second set. */}
        {routeSequenceOpen && routeSequenceBadgesOn && !storyBadgesShowing && routeSequenceBadgeData.features.length > 0 && (
          <Source id="route-sequence-badges" type="geojson" data={routeSequenceBadgeData}>
            {/* Centred on the exact camera / dwell position so the number reads as
                the point itself; colliding badges share one disc ("7–8"). */}
            <Layer
              id="route-sequence-badge-circles"
              type="circle"
              layout={{ 'circle-sort-key': ['get', 'sort'] }}
              paint={STACKED_BADGE_CIRCLE_PAINT as any}
            />
            <Layer
              id="route-sequence-badge-text"
              type="symbol"
              layout={STACKED_BADGE_TEXT_LAYOUT as any}
              paint={{ 'text-color': '#ffffff' }}
            />
          </Source>
        )}

        {/* RF Signal Markers - unclustered */}
        {showSignalMarkers && signalMarkers.length > 0 && !enableClustering && (
          <Source id="signal-markers" type="geojson" data={signalMarkersGeoJSON}>
            <Layer id="signal-wifi" type="circle"
              filter={['==', ['get', 'type'], 'wifi']}
              paint={{ 'circle-radius': 5, 'circle-color': '#42a5f5', 'circle-opacity': 0.7, 'circle-stroke-color': '#1565c0', 'circle-stroke-width': 1 }}
            />
            <Layer id="signal-bluetooth" type="circle"
              filter={['==', ['get', 'type'], 'bluetooth']}
              paint={{ 'circle-radius': 5, 'circle-color': '#26a69a', 'circle-opacity': 0.7, 'circle-stroke-color': '#00796b', 'circle-stroke-width': 1 }}
            />
            <Layer id="signal-cellular" type="circle"
              filter={['==', ['get', 'type'], 'cellular']}
              paint={{ 'circle-radius': 6, 'circle-color': '#7e57c2', 'circle-opacity': 0.7, 'circle-stroke-color': '#4527a0', 'circle-stroke-width': 1 }}
            />
          </Source>
        )}
        {/* Biometric HR-zone highlight overlay (driven from the Biometrics panel) */}
        {biometricHighlights.length > 0 && (
          <Source id="biometric-highlights" type="geojson" data={biometricHighlightsGeoJSON}>
            {/* Outer glow halo */}
            <Layer
              id="biometric-highlights-halo"
              type="circle"
              source="biometric-highlights"
              paint={{
                'circle-radius': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 6,
                  14, 12,
                  18, 22,
                ],
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.1,
                'circle-blur': 0.6,
              }}
            />
            {/* Translucent core dot */}
            <Layer
              id="biometric-highlights-core"
              type="circle"
              source="biometric-highlights"
              paint={{
                'circle-radius': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 2.5,
                  14, 5,
                  18, 9,
                ],
                'circle-color': ['get', 'color'],
                'circle-opacity': 0.55,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1,
                'circle-stroke-opacity': 0.55,
              }}
            />
          </Source>
        )}

        {/* RF Signal Markers - clustered */}
        {showSignalMarkers && signalMarkers.length > 0 && enableClustering && (
          <Source id="signal-markers-clustered" type="geojson" data={signalMarkersGeoJSON}
            cluster={true} clusterMaxZoom={14} clusterRadius={50}
          >
            <Layer id="signal-clusters" type="circle"
              filter={['has', 'point_count']}
              paint={{
                'circle-color': ['step', ['get', 'point_count'], '#0097a7', 20, '#00838f', 100, '#006064'],
                'circle-radius': ['step', ['get', 'point_count'], 15, 20, 20, 100, 25],
                'circle-opacity': 0.85,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': '#000000',
              }}
            />
            <Layer id="signal-clusters-text" type="symbol"
              filter={['has', 'point_count']}
              layout={{ 'text-field': '{point_count_abbreviated}', 'text-size': 11, 'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'] }}
              paint={{ 'text-color': '#fff' }}
            />
            <Layer id="signal-unclustered" type="circle"
              filter={['!', ['has', 'point_count']]}
              paint={{
                'circle-radius': 5,
                'circle-color': ['match', ['get', 'type'], 'wifi', '#2196f3', 'bluetooth', '#00bcd4', 'cellular', '#ff9800', '#888'],
                'circle-opacity': 0.75,
                'circle-stroke-color': '#000',
                'circle-stroke-width': 0.5,
              }}
            />
          </Source>
        )}

        {/* Itinerary Submissions (markers + Component labels) */}
        {showItinerary && itinerarySubmissions.length > 0 && (
          <Source id="itinerary-data" type="geojson" data={itineraryGeoJSON}>
            {/* Itinerary markers */}
            <Layer
              id="itinerary-markers"
              type="circle"
              source="itinerary-data"
              filter={['==', ['get', 'marker_type'], 'itinerary']}
              paint={{
                'circle-radius': [
                  'interpolate',
                  ['linear'],
                  ['zoom'],
                  8, 6,
                  12, 10,
                  16, 14,
                  20, 18
                ],
                'circle-color': '#00ff00', // Green color
                'circle-opacity': 0.8,
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2,
                'circle-stroke-opacity': 1
              }}
            />
            {/* Stop numbers centered on green dots (matches Itinerary tool) */}
            <Layer
              id="itinerary-numbers"
              type="symbol"
              source="itinerary-data"
              filter={['==', ['get', 'marker_type'], 'itinerary']}
              layout={{
                'text-field': ['to-string', ['get', 'stop_number']],
                'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                'text-size': [
                  'interpolate', ['linear'], ['zoom'],
                  8, 8,
                  12, 10,
                  16, 12,
                  20, 14,
                ],
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true,
              }}
              paint={{
                'text-color': '#ffffff',
                'text-halo-color': 'rgba(0,0,0,0.55)',
                'text-halo-width': 1,
              }}
            />
            {/* Component labels on itinerary points */}
            <Layer
              id="itinerary-labels"
              type="symbol"
              source="itinerary-data"
              filter={[
                'all',
                ['==', ['get', 'marker_type'], 'itinerary'],
                ['!=', ['get', 'route_component'], ''],
              ]}
              layout={{
                'text-field': ['get', 'route_component'],
                'text-font': ['DIN Pro Medium', 'Arial Unicode MS Bold'],
                'text-size': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 10,
                  14, 12,
                  18, 13,
                ],
                'text-offset': [0, -1.4],
                'text-anchor': 'bottom',
                'text-allow-overlap': false,
                'text-ignore-placement': false,
                'text-padding': 8,
                'text-max-width': 10,
              }}
              paint={{
                'text-color': '#00cc00',
                'text-halo-color': '#000000',
                'text-halo-width': 1.5,
                'text-halo-blur': 0.5,
              }}
            />
          </Source>
        )}

        <CameraLocationsMapLayers
          data={cameraLayerData}
          visible={showCameraLocations}
          cluster={cameraOverlay.display.cluster}
          showLabels={cameraOverlay.display.labels}
          size={cameraOverlay.display.size}
        />
        {showCameraLocations && streetViewEnabled && camerasInViewOpen && (() => {
          const strip = (
            <CamerasInViewStrip
              cameras={cameraLayerData}
              mapRef={mapRef}
              viewportKey={`${viewport.longitude.toFixed(5)},${viewport.latitude.toFixed(5)},${viewport.zoom.toFixed(2)}`}
              limit={streetViewSettings?.in_view_limit ?? 12}
              previews={streetViewPreviews}
              docked={Boolean(camerasInViewDockEl)}
              bottom={16}
              left={12}
              right={60}
              activeCameraId={
                popupInfo?.type === 'camera' ? String(popupInfo.properties?.camera_id ?? '') || null : null
              }
              onClose={() => setCamerasInViewOpen(false)}
              onSelect={(item: CameraInView) => {
                mapRef.current?.flyTo({ center: [item.lon, item.lat], zoom: Math.max(viewport.zoom, 16), duration: 600 });
                setPopupInfo({
                  longitude: item.lon,
                  latitude: item.lat,
                  layerId: CAMERA_OVERLAY_LAYER_IDS.unclustered,
                  type: 'camera',
                  properties: { ...item.camera, marker_type: 'camera' },
                });
              }}
              callouts={{
                calloutKey: cameraCalloutKey,
                pinnedKeys: cameraCalloutKeys,
                visible: cameraCalloutsVisible,
                onTogglePin: toggleCameraCallout,
                onToggleVisible: () => setCameraCalloutsVisible((v) => !v),
                onClear: () => setCameraCallouts([]),
              }}
              sequence={cameraSequenceRank.size > 0 ? { key: cameraCalloutKey, rank: cameraSequenceRank } : undefined}
            />
          );
          // Dock onto the timeline card when the page offers a slot.
          return camerasInViewDockEl ? createPortal(strip, camerasInViewDockEl) : strip;
        })()}

        {/* Digital PoL Activity Markers - Using Mapbox Maki Symbols */}
        {showDigitalPoL && digitalPoLData.length > 0 && (
          <Source 
            id="digital-pol-data" 
            type="geojson" 
            data={{
              type: 'FeatureCollection',
              features: digitalPoLData
                .filter(item => item.latitude && item.longitude)
                .map((item, index) => ({
                  type: 'Feature',
                  properties: {
                    id: item.id || `pol-${index}`,
                    entity_id: item.entity_id,
                    entity_type: item.entity_type,
                    activity_type: item.activity_type,
                    timestamp: item.timestamp,
                    timeString: item.timeString || new Date(item.timestamp).toLocaleString(),
                    activity_data: JSON.stringify(item.activity_data),
                    // Map activity types to Maki icon names
                    icon: item.activity_type === 'app_info' ? 'mobile-phone' :
                          item.activity_type === 'app_usage' ? 'star' :
                          item.activity_type === 'browsing' ? 'globe' :
                          item.activity_type === 'call' ? 'telephone' :
                          item.activity_type === 'sms' ? 'communications-tower' :
                          item.activity_type === 'status' ? 'fuel' : 'marker',
                    // Map activity types to colors
                    color: item.activity_type === 'app_info' ? '#3b82f6' :
                           item.activity_type === 'app_usage' ? '#8b5cf6' :
                           item.activity_type === 'browsing' ? '#f59e0b' :
                           item.activity_type === 'call' ? '#10b981' :
                           item.activity_type === 'sms' ? '#06b6d4' :
                           item.activity_type === 'status' ? '#ef4444' : '#9e9e9e'
                  },
                  geometry: {
                    type: 'Point',
                    coordinates: [item.longitude, item.latitude]
                  }
                }))
            }}
          >
            <Layer
              id="digital-pol-icons"
              type="symbol"
              source="digital-pol-data"
              layout={{
                'icon-image': ['get', 'icon'],
                'icon-size': [
                  'interpolate', ['linear'], ['zoom'],
                  10, 0.8,
                  15, 1.2,
                  20, 1.5
                ],
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'text-field': '',
                'text-allow-overlap': true
              }}
              paint={{
                'icon-color': ['get', 'color'],
                'icon-halo-color': '#ffffff',
                'icon-halo-width': 2
              }}
            />
          </Source>
        )}

        {/* Circle Area Search Layers */}
        {drawCircles.length > 0 && (
          <>
            {/* Circle Outlines */}
            <Source id="area-circles" type="geojson" data={circlesGeoJSON}>
              <Layer
                id="circle-outlines"
                type="line"
                source="area-circles"
                paint={{
                  'line-color': [
                    'case',
                    ['get', 'hasResults'], '#ff9800', // Orange if has results
                    '#2196f3'  // Blue if no results yet
                  ],
                  'line-width': 3,
                  'line-opacity': 0.8
                }}
              />
              <Layer
                id="circle-fills"
                type="fill"
                source="area-circles"
                paint={{
                  'fill-color': [
                    'case',
                    ['get', 'hasResults'], '#ff9800', // Orange if has results
                    '#2196f3'  // Blue if no results yet
                  ],
                  'fill-opacity': 0.1
                }}
              />
            </Source>

            {/* Circle Centers and Labels */}
            <Source id="circle-centers" type="geojson" data={circleCentersGeoJSON}>
              <Layer
                id="circle-centers"
                type="circle"
                source="circle-centers"
                paint={{
                  'circle-radius': 6,
                  'circle-color': [
                    'case',
                    ['get', 'hasResults'], '#ff9800', // Orange if has results
                    '#2196f3'  // Blue if no results yet
                  ],
                  'circle-stroke-color': '#ffffff',
                  'circle-stroke-width': 2,
                  'circle-opacity': 0.9
                }}
              />
              <Layer
                id="circle-labels"
                type="symbol"
                source="circle-centers"
                layout={{
                  'text-field': [
                    'case',
                    ['get', 'hasResults'],
                    ['concat', ['get', 'name'], '\n(', ['to-string', ['get', 'resultCount']], ' entities)'],
                    ['get', 'name']
                  ],
                  'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                  'text-size': 12,
                  'text-offset': [0, 2],
                  'text-anchor': 'top'
                }}
                paint={{
                  'text-color': '#333333',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 1
                }}
              />
            </Source>
          </>
        )}

        {/* Saved Geofences (Pathfinder Geofence tab) */}
        {geofencesGeoJSON.features.length > 0 && (
          <Source id="pf-geofences" type="geojson" data={geofencesGeoJSON}>
            <Layer
              id="pf-geofences-fill"
              type="fill"
              source="pf-geofences"
              paint={{
                'fill-color': ['case', ['get', 'selected'], '#e91e63', '#00bcd4'],
                'fill-opacity': ['case', ['get', 'selected'], 0.18, 0.08],
              }}
            />
            <Layer
              id="pf-geofences-outline"
              type="line"
              source="pf-geofences"
              paint={{
                'line-color': ['case', ['get', 'selected'], '#e91e63', '#00bcd4'],
                'line-width': ['case', ['get', 'selected'], 3, 2],
                'line-opacity': 0.9,
              }}
            />
            <Layer
              id="pf-geofences-labels"
              type="symbol"
              source="pf-geofences"
              layout={{
                'text-field': ['get', 'name'],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 12,
              }}
              paint={{
                'text-color': '#006064',
                'text-halo-color': '#ffffff',
                'text-halo-width': 1.5,
              }}
            />
          </Source>
        )}

        {/* Overwatch media article markers (geolocated GDELT news) */}
        {overwatchGeoJSON.features.length > 0 && (
          <Source id="pf-overwatch" type="geojson" data={overwatchGeoJSON}>
            <Layer
              id="pf-overwatch-halo"
              type="circle"
              source="pf-overwatch"
              paint={{
                'circle-radius': 9,
                'circle-color': ['case', ['get', 'foreign'], '#3f51b5', '#ff9800'],
                'circle-opacity': 0.25,
              }}
            />
            <Layer
              id="pf-overwatch-dot"
              type="circle"
              source="pf-overwatch"
              paint={{
                'circle-radius': 4,
                'circle-color': ['case', ['get', 'foreign'], '#3f51b5', '#ff9800'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 1.5,
              }}
            />
          </Source>
        )}

        {/* AdTech (commercial mobile-location) signal points from Area Search */}
        {adtechGeoJSON.features.length > 0 && (
          <Source id="pf-adtech" type="geojson" data={adtechGeoJSON}>
            <Layer
              id="pf-adtech-halo"
              type="circle"
              source="pf-adtech"
              paint={{
                'circle-radius': ['case', ['get', 'focused'], 9, 5],
                'circle-color': ['get', 'color'],
                'circle-opacity': ['case', ['get', 'focused'], 0.35, 0.15],
              }}
            />
            <Layer
              id="pf-adtech-dot"
              type="circle"
              source="pf-adtech"
              paint={{
                'circle-radius': ['case', ['get', 'focused'], 4, 2.5],
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': ['case', ['get', 'focused'], 1.5, 0.75],
              }}
            />
          </Source>
        )}

        {/* Annotation Polygon Being Drawn */}
        {isAnnotationMode && annotationPolygonGeoJSON && (
          <Source id="annotation-drawing" type="geojson" data={annotationPolygonGeoJSON}>
            {annotationVertices.length >= 3 ? (
              <>
                <Layer
                  id="annotation-polygon-fill"
                  type="fill"
                  paint={{
                    'fill-color': '#e91e63',
                    'fill-opacity': 0.2
                  }}
                />
                <Layer
                  id="annotation-polygon-outline"
                  type="line"
                  paint={{
                    'line-color': '#e91e63',
                    'line-width': 2,
                    'line-dasharray': [2, 2]
                  }}
                />
              </>
            ) : (
              <Layer
                id="annotation-line"
                type="line"
                paint={{
                  'line-color': '#e91e63',
                  'line-width': 2,
                  'line-dasharray': [2, 2]
                }}
              />
            )}
          </Source>
        )}

        {/* Annotation Vertices */}
        {isAnnotationMode && annotationVertices.length > 0 && (
          <Source 
            id="annotation-vertices" 
            type="geojson" 
            data={{
              type: 'FeatureCollection',
              features: annotationVertices.map((v, i) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: v },
                properties: { index: i, isFirst: i === 0 }
              }))
            }}
          >
            <Layer
              id="annotation-vertices-layer"
              type="circle"
              paint={{
                'circle-radius': ['case', ['get', 'isFirst'], 8, 5],
                'circle-color': ['case', ['get', 'isFirst'], '#4caf50', '#e91e63'],
                'circle-stroke-color': '#ffffff',
                'circle-stroke-width': 2
              }}
            />
          </Source>
        )}

        {/* Camera / reference-layer popups — shared intel card (Street View thumbnails, layer styling). */}
        {popupInfo && intelPopupOpen && (() => {
          const close = () => { setPopupInfo(null); setPopupActionMsg(null); };
          const p = (popupInfo.properties || {}) as Record<string, any>;
          const lat = Number(popupInfo.latitude);
          const lon = Number(popupInfo.longitude);
          const coords = Number.isFinite(lat) && Number.isFinite(lon) ? { lat, lon } : null;
          const streetView = (): IntelAction[] =>
            streetViewEnabled && coords ? [{ label: 'Street View', href: streetViewDeepLink(lat, lon) }] : [];
          let card: React.ReactNode;
          if (popupInfo.type === 'reference') {
            const refView = referenceLayers.layers.find((v) => v.layer.id === popupInfo.referenceLayerId) ?? null;
            card = refView ? (
              <ReferenceFeaturePopupContent
                layer={refView.layer}
                style={refView.style}
                properties={p}
                coords={coords}
                onClose={close}
                actions={streetView()}
              />
            ) : (
              <IntelPopupCard title="Reference feature" onClose={close} coords={coords} />
            );
          } else {
            const camera = parseCameraFeatureProperties(popupInfo.properties);
            card = camera ? (
              <CameraLocationPopupContent camera={camera} onClose={close} />
            ) : (
              <IntelPopupCard title="Camera" onClose={close} coords={coords} />
            );
          }
          return (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              onClose={close}
              closeButton={false}
              closeOnClick={false}
              anchor="bottom"
              offset={12}
              className={INTEL_POPUP_CLASS}
              maxWidth="340px"
            >
              {card}
            </Popup>
          );
        })()}

        {/* Popup — dark command card. Chrome is painted via useDarkMapPopupChrome
            because Mapbox's stylesheet regularly beats app CSS for .mapboxgl-popup-content. */}
        {popupInfo && !intelPopupOpen && (
          <Popup
            longitude={popupInfo.longitude}
            latitude={popupInfo.latitude}
            onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
            closeButton={false}
            closeOnClick={false}
            anchor="bottom"
            className={DARK_MAP_POPUP_CLASS}
            maxWidth="320px"
            style={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
          >
              {popupInfo.type === 'pinpoint' ? (
                <PfPopupShell
                  accent="#90caf9"
                  title={popupInfo.isLoading
                    ? 'Resolving…'
                    : (popupInfo.placeName || popupInfo.address || 'Pinned location')}
                  badge="PIN"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  {popupInfo.address && popupInfo.placeName && popupInfo.address !== popupInfo.placeName && (
                    <div style={{ marginBottom: 6, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>{popupInfo.address}</div>
                  )}
                  <PfPopupRow label="MGRS" mono>{popupInfo.mgrs || 'n/a'}</PfPopupRow>
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : (popupInfo.layerId === 'pf-adtech-dot' || popupInfo.layerId === 'pf-adtech-halo') ? (
                <PfPopupShell
                  accent="#f06292"
                  title={String(popupInfo.properties.registration_id || 'AdTech').replace(/^GN-/i, '').slice(0, 12)}
                  badge="ADTECH"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                  actions={
                    <>
                      <AdtechDeviceActions
                        registrationId={String(popupInfo.properties.registration_id || '')}
                        courseId={courseId}
                        onLoadPol={onLoadAdtechPol}
                        polLoading={adtechPolLoading}
                        iconFontSize={15}
                      />
                    </>
                  }
                >
                  {popupInfo.properties.timestamp && (
                    <PfPopupRow label="Seen">
                      {new Date(popupInfo.properties.timestamp).toLocaleString()}
                    </PfPopupRow>
                  )}
                  {popupInfo.properties.accuracy != null && (
                    <PfPopupRow label="Acc.">{Math.round(Number(popupInfo.properties.accuracy))} m</PfPopupRow>
                  )}
                  {popupInfo.properties.carrier && (
                    <PfPopupRow label="Carrier">{popupInfo.properties.carrier}</PfPopupRow>
                  )}
                  {popupInfo.properties.forensic_flag && (
                    <PfPopupRow label="Flag">{popupInfo.properties.forensic_flag}</PfPopupRow>
                  )}
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : popupInfo.properties && popupInfo.properties.type === 'co-traveler-encounter' ? (
                <PfPopupShell
                  accent="#64b5f6"
                  title="Co-Traveler"
                  badge="ENCOUNTER"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  <PfPopupRow label="Primary">{popupInfo.properties.primaryDevice}</PfPopupRow>
                  <PfPopupRow label="Partner">{popupInfo.properties.coTravelerDevice}</PfPopupRow>
                  <PfPopupRow label="Seen">{new Date(popupInfo.properties.timestamp).toLocaleString()}</PfPopupRow>
                  <PfPopupRow label="Dur.">{popupInfo.properties.duration.toFixed(1)} min</PfPopupRow>
                  <PfPopupRow label="Range">{popupInfo.properties.proximity.toFixed(0)}m</PfPopupRow>
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : popupInfo.properties?.type === 'co-traveler-connection' ? (
                <PfPopupShell
                  accent="#ffb74d"
                  title="Connection"
                  badge="LINK"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  <PfPopupRow label="Pair">{popupInfo.properties.primaryDevice} ↔ {popupInfo.properties.coTravelerDevice}</PfPopupRow>
                  <PfPopupRow label="Hits">{popupInfo.properties.encounters}</PfPopupRow>
                  <PfPopupRow label="Dur.">{popupInfo.properties.totalDuration.toFixed(1)} min</PfPopupRow>
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : popupInfo.properties?.ptt ? (
                <PfPopupShell
                  accent={popupInfo.properties.color || '#29b6f6'}
                  title={popupInfo.properties.sender || 'PTT'}
                  badge="PTT"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                  actions={onPttOpenInPanel ? (
                    <button
                      type="button"
                      style={pfActionBtnStyle(true)}
                      onClick={() => {
                        onPttOpenInPanel(Number(popupInfo.properties.id));
                        setPopupInfo(null);
                      }}
                    >
                      Open in panel
                    </button>
                  ) : undefined}
                >
                  {popupInfo.properties.team && <PfPopupRow label="Team">{popupInfo.properties.team}</PfPopupRow>}
                  {popupInfo.properties.started_at && (
                    <PfPopupRow label="Sent">{new Date(popupInfo.properties.started_at).toLocaleString()}</PfPopupRow>
                  )}
                  <div style={{ marginTop: 4 }}>{popupInfo.properties.transcript}</div>
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : popupInfo.properties?.activity_type ? (
                <PfPopupShell
                  accent={popupInfo.properties.color || '#ef5350'}
                  title={
                    popupInfo.properties.activity_type === 'app_info' ? 'App Info' :
                    popupInfo.properties.activity_type === 'app_usage' ? 'App Usage' :
                    popupInfo.properties.activity_type === 'browsing' ? 'Browsing' :
                    popupInfo.properties.activity_type === 'call' ? 'Call' :
                    popupInfo.properties.activity_type === 'sms' ? 'SMS' :
                    popupInfo.properties.activity_type === 'status' ? 'Status' : 'Activity'
                  }
                  badge="POL"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  <PfPopupRow label="Entity">{popupInfo.properties.entity_id}</PfPopupRow>
                  <PfPopupRow label="Seen">
                    {popupInfo.properties.timeString || new Date(popupInfo.properties.timestamp).toLocaleString()}
                  </PfPopupRow>
                  {popupInfo.properties.activity_data && (
                    <div style={{ margin: '2px 0 0 4px', fontSize: '0.7rem', color: 'rgba(255,255,255,0.65)' }}>
                      {(() => {
                        try {
                          const data = JSON.parse(popupInfo.properties.activity_data);
                          return Object.entries(data).slice(0, 4).map(([key, value]: [string, any]) => (
                            <div key={key}>• {key}: {String(value || 'N/A').substring(0, 50)}</div>
                          ));
                        } catch {
                          return <div>• {String(popupInfo.properties.activity_data).substring(0, 100)}</div>;
                        }
                      })()}
                    </div>
                  )}
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : popupInfo.properties?.name && popupInfo.properties?.radius ? (
                <PfPopupShell
                  accent="#64b5f6"
                  title={popupInfo.properties.name}
                  badge="AREA"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  <PfPopupRow label="Radius">{popupInfo.properties.radius}m</PfPopupRow>
                  {popupInfo.properties.hasResults ? (
                    <PfPopupRow label="Hits">{popupInfo.properties.resultCount} entities</PfPopupRow>
                  ) : (
                    <div style={{ color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>No search performed yet</div>
                  )}
                  <PfPopupCoords
                    lat={popupInfo.properties.centerLat ?? popupInfo.latitude}
                    lon={popupInfo.properties.centerLon ?? popupInfo.longitude}
                  />
                </PfPopupShell>
              ) : popupInfo.properties?.marker_type === 'itinerary' ? (
                <PfPopupShell
                  accent="#81c784"
                  title={popupInfo.properties.location_name || 'Itinerary'}
                  badge="ROUTE"
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                >
                  <PfPopupRow label={singular}>{popupInfo.properties.student_number}</PfPopupRow>
                  <PfPopupRow label="Type">{popupInfo.properties.route_type}</PfPopupRow>
                  <PfPopupRow label="Arrive">{new Date(popupInfo.properties.arrive_datetime).toLocaleString()}</PfPopupRow>
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                </PfPopupShell>
              ) : (popupInfo.layerId === 'dwell-circles' || popupInfo.layerId === 'dwell-labels') ? (
                <PfPopupShell
                  accent="#ef5350"
                  title={popupInfo.properties.place_name || popupInfo.properties.entity_id || 'Dwell'}
                  badge={popupInfo.properties.device_type
                    ? String(popupInfo.properties.device_type).toUpperCase()
                    : 'DWELL'}
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                  actions={
                    <>
                      <button
                        type="button"
                        style={pfActionBtnStyle(true)}
                        title={dwellInsetKeys.has(dwellInsetKey(
                          Number(popupInfo.properties.id),
                          Number(popupInfo.properties.longitude ?? popupInfo.longitude),
                          Number(popupInfo.properties.latitude ?? popupInfo.latitude),
                        ))
                          ? 'Remove the zoomed-in map inset for this dwell'
                          : 'Pin a zoomed-in map inset of this dwell for screenshots'}
                        onClick={(e) => {
                          e.stopPropagation();
                          const p = popupInfo.properties;
                          const dLat = Number(p.latitude ?? popupInfo.latitude);
                          const dLon = Number(p.longitude ?? popupInfo.longitude);
                          const dwellRow: PathfinderMapDwellLocation =
                            (filteredDwellLocations as PathfinderMapDwellLocation[]).find((d) => d.id === Number(p.id)) || {
                              id: Number(p.id),
                              entity_id: String(p.entity_id ?? ''),
                              device_type: p.device_type,
                              duration_seconds: Number(p.duration_seconds || 0),
                              start_time: String(p.start_time ?? ''),
                              end_time: String(p.end_time ?? ''),
                              longitude: dLon,
                              latitude: dLat,
                              place_name: p.place_name || undefined,
                              place_address: p.place_address || undefined,
                            };
                          toggleDwellInset(dwellRow);
                        }}
                      >
                        {dwellInsetKeys.has(dwellInsetKey(
                          Number(popupInfo.properties.id),
                          Number(popupInfo.properties.longitude ?? popupInfo.longitude),
                          Number(popupInfo.properties.latitude ?? popupInfo.latitude),
                        ))
                          ? 'Remove inset'
                          : 'Add inset'}
                      </button>
                      {popupInfo.properties.entity_id ? (
                        <button
                          type="button"
                          style={pfActionBtnStyle(true)}
                          disabled={popupActionBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            flagEntityToWatchlist(
                              String(popupInfo.properties.entity_id),
                              String(popupInfo.properties.place_name || popupInfo.properties.entity_id),
                            );
                          }}
                        >
                          Flag
                        </button>
                      ) : null}
                    </>
                  }
                >
                  <PfPopupRow label="Entity" mono>{popupInfo.properties.entity_id}</PfPopupRow>
                  <PfPopupRow label="Dwell">
                    {(() => {
                      const s = Number(popupInfo.properties.duration_seconds || 0);
                      const h = Math.floor(s / 3600);
                      const m = Math.floor((s % 3600) / 60);
                      if (h > 0) return `${h}h ${m}m`;
                      if (m > 0) return `${m}m`;
                      return `${Math.floor(s)}s`;
                    })()}
                  </PfPopupRow>
                  {popupInfo.properties.start_time && (
                    <PfPopupRow label="Seen">
                      {new Date(popupInfo.properties.start_time).toLocaleString()}
                      {popupInfo.properties.end_time && (
                        <span style={{ display: 'block', marginTop: 1, fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' }}>
                          → {new Date(popupInfo.properties.end_time).toLocaleString()}
                        </span>
                      )}
                    </PfPopupRow>
                  )}
                  {popupInfo.properties.place_address && (
                    <PfPopupRow label="Addr">{popupInfo.properties.place_address}</PfPopupRow>
                  )}
                  <PfPopupCoords
                    lat={Number(popupInfo.properties.latitude ?? popupInfo.latitude)}
                    lon={Number(popupInfo.properties.longitude ?? popupInfo.longitude)}
                  />
                  {popupActionMsg && (
                    <div style={{ marginTop: 6, fontSize: '0.65rem', color: '#69f0ae' }}>{popupActionMsg}</div>
                  )}
                </PfPopupShell>
              ) : (
                <PfPopupShell
                  accent="#69f0ae"
                  title={popupInfo.properties?.studentId || popupInfo.properties?.deviceId || 'Track'}
                  badge={popupInfo.properties?.deviceType
                    ? String(popupInfo.properties.deviceType).toUpperCase()
                    : 'POINT'}
                  onClose={() => { setPopupInfo(null); setPopupActionMsg(null); }}
                  actions={
                    popupInfo.properties?.studentId ? (
                      <button
                        type="button"
                        style={pfActionBtnStyle(true)}
                        disabled={popupActionBusy}
                        onClick={(e) => {
                          e.stopPropagation();
                          flagEntityToWatchlist(String(popupInfo.properties.studentId));
                        }}
                      >
                        Flag
                      </button>
                    ) : null
                  }
                >
                  {popupInfo.properties?.deviceId && (
                    <PfPopupRow label="Device" mono>{popupInfo.properties.deviceId}</PfPopupRow>
                  )}
                  <PfPopupRow label="Seen">
                    {popupInfo.properties?.timeString || (popupInfo.properties?.timestamp
                      ? new Date(popupInfo.properties.timestamp).toLocaleString()
                      : '—')}
                  </PfPopupRow>
                  {popupInfo.properties?.accuracy != null && (
                    <PfPopupRow label="Acc.">{Math.round(Number(popupInfo.properties.accuracy))} m</PfPopupRow>
                  )}
                  {popupInfo.properties?.place_name && (
                    <PfPopupRow label="Place">{popupInfo.properties.place_name}</PfPopupRow>
                  )}
                  <PfPopupCoords lat={popupInfo.latitude} lon={popupInfo.longitude} />
                  {popupActionMsg && (
                    <div style={{ marginTop: 6, fontSize: '0.65rem', color: '#69f0ae' }}>{popupActionMsg}</div>
                  )}
                </PfPopupShell>
              )}
          </Popup>
        )}
        
        {/* Map Legend */}
        <MapLegend
          isVisible={showLegend}
          onClose={() => setShowLegend(false)}
          showDataPoints={showDataPoints}
          showRouteLines={showRouteLines}
          showDirectionArrows={showDirectionArrows}
          showHeatmap={showHeatmap}
          showDwells={showDwells}
          showCoTravelerPoints={_showCoTravelerPoints}
          showCoTravelerLines={_showCoTravelerLines}
          showCoTravelerHeatmap={_showCoTravelerHeatmap}
          showDigitalPoL={showDigitalPoL}
          showItinerary={showItinerary}
          showCameraLocations={showCameraLocations}
          cameraLocationCount={legendCameraCount}
          cameraCategories={legendCameraCategories}
          referenceLayers={referenceLayers.enabledLayers}
          showAreaCircles={drawCircles.length > 0}
          studentColors={legendStudentColors}
          coTravelerCount={coTravelerResults.length}
          dwellCount={legendScope ? legendScope.dwellsInView : dwellLocations.length}
          areaCircleCount={drawCircles.filter(c => c.visible).length}
          selectedTraineeName={selectedTraineeName}
          selectedTraineeDevices={selectedTraineeDevices}
          coTravelerLegendItems={legendCoTravelerItems}
          uploadedFiles={legendUploadedFiles}
          onToggleUploadedFile={onToggleUploadedFile}
          scopedToView={Boolean(legendScope)}
        />

        {/* GPS Cleanup draw control (admin-only) - MUST be a child of
            <Map> because react-map-gl's useControl needs to find the
            map instance in its parent tree. The control is cheap to
            mount; it only enters draw_polygon mode when `active=true`. */}
        {canUseGpsCleanup && (
          <PathfinderCleanupDrawControl
            active={cleanupOpen && cleanupPinVerified}
            onPolygonChange={setCleanupPolygon}
            resetSignal={cleanupResetSignal}
          />
        )}
      </Map>

      {(isMeasuring || measurementPoints.length > 0) && (
        <MapMeasureReadout
          mode={measureMode}
          onModeChange={changeMeasureMode}
          points={measurementPoints}
          ringSpacingM={ringSpacingM}
          onRingSpacing={setRingSpacingM}
          ringCount={ringCount}
          onRingCount={setRingCount}
          ringOpacity={ringOpacity}
          onRingOpacity={setRingOpacity}
        />
      )}

      {/* Pinned camera callouts (Street View cards with a wedge to the camera).
          Sibling of <Map>, positioned via map.project(), so dragging a card
          never pans the map and clicks never reach the map's click handler. */}
      {showCameraLocations && cameraCalloutsVisible && cameraCallouts.length > 0 && (
        <CameraCallouts
          callouts={cameraCallouts}
          mapRef={mapRef}
          previews={streetViewPreviews}
          anchor={badgesShowing ? 'ring' : 'dot'}
          onOffsetChange={moveCameraCallout}
          onResize={resizeCameraCallout}
          onRemove={removeCameraCallout}
          onSelect={(item) =>
            setPopupInfo({
              longitude: item.lon,
              latitude: item.lat,
              layerId: CAMERA_OVERLAY_LAYER_IDS.unclustered,
              type: 'camera',
              properties: { ...item.camera, marker_type: 'camera' },
            })
          }
        />
      )}

      {/* Dwell insets: on-demand zoomed-in mini maps with a leader to the dwell.
          Sibling of <Map> like the camera callouts; each inset's map only
          mounts (and fetches tiles) once the user pins it. */}
      {isFullscreen && showDwells && dwellInsetsVisible && dwellInsets.length > 0 && (
        <DwellInsets
          insets={dwellInsets}
          mapRef={mapRef}
          mapStyle={resolvedMapStyle}
          anchor={badgesShowing ? 'ring' : 'dot'}
          onOffsetChange={moveDwellInset}
          onResize={resizeDwellInset}
          onZoom={zoomDwellInset}
          onToggleImagery={toggleDwellInsetImagery}
          onRemove={removeDwellInset}
        />
      )}

      {/* Route sequence overlay: abbreviated ordered list with CSV export / copy. */}
      {routeSequenceOpen && (
        <RouteSequencePanel
          groups={routeSequenceGroups}
          radiusM={cameraNearTracks.radiusM}
          camerasIncluded={showCameraLocations}
          dwellsIncluded={showDwells}
          showBadges={routeSequenceBadgesOn}
          onToggleBadges={() => setRouteSequenceBadgesOn((v) => !v)}
          onClose={() => setRouteSequenceOpen(false)}
          onSelect={flyToSequenceEntry}
          timeZone={routeSequenceTz}
        />
      )}

      {/* GPS Cleanup panel (admin-only). Lives OUTSIDE <Map> so it has
          normal pointer events / scrolling / focus and doesn't interact
          with the map's gesture handlers. The polygon and time window
          are passed in; the panel handles PIN entry and the action flow
          on its own. */}
      {canUseGpsCleanup && (
        <PathfinderCleanupPanel
          open={cleanupOpen}
          onClose={() => {
            setCleanupOpen(false);
            setCleanupPolygon(null);
            setCleanupPinVerified(false);
          }}
          polygon={cleanupPolygon}
          startDate={timeRange?.start || new Date(Date.now() - 24 * 60 * 60 * 1000)}
          endDate={timeRange?.end || new Date()}
          availableEntityIds={Array.from(new Set(
            (routes || []).map((r) => r.studentId).filter(Boolean) as string[],
          ))}
          onCleanupComplete={() => {
            // Bubble up to PathfinderPage so it can re-fetch tracks +
            // dwells. Without this, quarantined rows still show on the
            // map until the user nudges the time slider. The page is
            // the data owner so it has to be the one to refetch.
            try {
              onRefreshDataRequested?.();
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[Pathfinder Cleanup] Auto-refresh failed:', e);
            }
          }}
          onResetPolygon={() => {
            setCleanupPolygon(null);
            setCleanupResetSignal((s) => s + 1);
          }}
          onPinVerifiedChange={setCleanupPinVerified}
        />
      )}
    </Box>
  );
};

export default PathfinderMapGL;
